import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/common/DashboardLayout';
import StatusBadge from '../components/common/StatusBadge';
import Pagination from '../components/common/Pagination';
import { adminAPI } from '../services/api';
import MonitoringMapCard, { PhotoCarousel } from '../components/common/MonitoringMapCard';
import PhotoLightbox from '../components/common/PhotoLightbox';
import ProjectSpeciesSection from '../components/common/ProjectSpeciesSection';
import AdminSpecies from './AdminSpecies';
import SpeciesReport from './SpeciesReport';

const PHOTO_BASE = window.location.origin;
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${PHOTO_BASE}${url}`;
};

function ZoneFlyTo({ zones, entries, selectedZoneId }) {
  const map = useMap();
  React.useEffect(() => {
    if (!selectedZoneId) return;
    const zone = (zones || []).find(z => z.id === selectedZoneId);
    if (!zone) return;
    const pts = [];
    if (zone.boundary_polygon) zone.boundary_polygon.forEach(p => pts.push([p.lat, p.lng]));
    if (zone.center_latitude && zone.center_longitude) pts.push([zone.center_latitude, zone.center_longitude]);
    (entries || []).filter(e => e.zone_id === selectedZoneId).forEach(e => {
      if (e.latitude && e.longitude) pts.push([e.latitude, e.longitude]);
      (e.photos || []).forEach(p => { if (p.latitude && p.longitude) pts.push([p.latitude, p.longitude]); });
    });
    if (pts.length > 0) {
      const b = L.latLngBounds(pts).pad(0.15);
      map.flyToBounds(b, { duration: 0.8, maxZoom: 18 });
    }
  }, [selectedZoneId, zones, entries, map]);
  return null;
}

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Pie, Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler
);

const navItems = [
  { path: '', label: 'Dashboard', icon: '\uD83D\uDCCA' },
  { section: 'Management' },
  { path: '/users', label: 'Users', icon: '\uD83D\uDC65' },
  { path: '/field-supervisors', label: 'Field Supervisors', icon: '\uD83E\uDDD1\u200D\uD83C\uDF3F' },
  { path: '/targets', label: 'Project Moderation', icon: '\uD83D\uDE80' },
  { path: '/reports', label: 'Report Review', icon: '\uD83D\uDCCB' },
  { path: '/ideas', label: 'Idea Moderation', icon: '\uD83D\uDCA1' },
  { path: '/group-monitoring', label: 'Group Monitoring', icon: '\uD83C\uDF33' },
  { path: '/monitoring-progress', label: 'Monitoring Progress', icon: '\uD83D\uDCCA' },
  { path: '/allocations', label: 'Project Assignments', icon: '\uD83D\uDD17' },
  { path: '/zone-management', label: 'Zone Management', icon: '\uD83D\uDCCD' },
  { path: '/species', label: 'Species Management', icon: '\uD83C\uDF3F' },
  { path: '/species-report', label: 'Species Report', icon: '\uD83C\uDF3B' },
  { section: 'Content' },
  { path: '/blog', label: 'Blog Management', icon: '\uD83D\uDCDD' },
  { path: '/blog-moderation', label: 'Blog Moderation', icon: '\u2705' },
  { section: 'Finance' },
  { path: '/disbursements', label: 'Disbursements', icon: '\uD83D\uDCB0' },
];

// ---------------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------------
function ChartCard({ title, subtitle, children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '14px', padding: '24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}>
      <div style={{
        fontSize: '16px', fontWeight: 600, color: '#1a1a2e',
        marginBottom: subtitle ? '4px' : '12px',
        paddingBottom: subtitle ? '4px' : '12px',
        borderBottom: subtitle ? 'none' : '1px solid #f0f0f0',
      }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
          {subtitle}
        </div>
      )}
      {children}
    </div>
  );
}

function KpiCard({ icon, value, label, sublabel, color, trend }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '14px', padding: '20px',
      display: 'flex', alignItems: 'center', gap: '16px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      borderLeft: `4px solid ${color}`,
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}>
      <div style={{
        fontSize: '28px', width: '52px', height: '52px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '12px', background: `${color}15`, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{label}</div>
        {sublabel && (
          <div style={{
            fontSize: '11px', marginTop: '2px', fontWeight: 500,
            color: trend === 'up' ? '#16a34a' : trend === 'down' ? '#dc2626' : '#9ca3af',
          }}>
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressIndicator({ label, value, max, color, unit = '' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
        <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
        <span style={{ color: '#6b7280' }}>{value.toLocaleString()}{unit} / {max.toLocaleString()}{unit}</span>
      </div>
      <div style={{ background: '#f3f4f6', borderRadius: '8px', height: '10px', overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: `linear-gradient(90deg, ${color}, ${color}cc)`,
          borderRadius: '8px', transition: 'width 1s ease',
        }} />
      </div>
      <div style={{ textAlign: 'right', fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>{pct.toFixed(1)}%</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview (Chart-Rich Dashboard)
// ---------------------------------------------------------------------------
function Overview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getDashboard()
      .then(res => setStats(res.data))
      .catch(() => toast.error('Failed to load dashboard stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner">Loading...</div>;

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const totalUsers = stats?.total_users ?? 0;
  const pendingUsers = stats?.pending_users ?? 0;
  const totalCsr = stats?.total_csr ?? 0;
  const totalAgencies = stats?.total_agencies ?? 0;
  const totalIndividuals = stats?.total_individuals ?? 0;
  const pendingTargets = stats?.pending_targets ?? 0;
  const pendingReports = stats?.pending_reports ?? 0;
  const pendingIdeas = stats?.pending_ideas ?? 0;
  const pendingBlogPosts = stats?.pending_blog_posts ?? 0;
  const pendingGroupMonitoring = stats?.pending_group_monitoring ?? 0;
  const totalTargets = stats?.total_targets ?? 0;

  const totalPlantsReported = stats?.total_plants_reported ?? 0;
  const treesPlanted = totalPlantsReported || (stats?.metrics?.total_trees_planted ?? 0);
  const carbonKg = stats?.metrics?.carbon_sequestration_kg || (treesPlanted * 12);
  const totalPlantsTarget = stats?.total_plants_target ?? 0;
  const avgSurvival = stats?.avg_survival_rate ?? 0;
  const totalFundsDisbursed = stats?.total_funds_disbursed ?? 0;
  const totalFundsPending = stats?.total_funds_pending ?? 0;
  const totalApplications = stats?.total_applications ?? 0;
  const acceptedApplications = stats?.accepted_applications ?? 0;

  const targetStatus = stats?.target_status ?? {};
  const reportStatus = stats?.report_status ?? {};
  const stateData = stats?.state_data ?? [];
  const allocationStatus = stats?.allocation_status ?? {};
  const projectProgress = stats?.project_progress ?? [];
  const fundByMilestone = stats?.fund_by_milestone ?? {};
  const totalTreesMonitored = stats?.total_trees_monitored ?? 0;
  const totalMonitoringEntries = stats?.total_monitoring_entries ?? 0;
  const monitoringAvgSurvival = stats?.monitoring_avg_survival ?? 0;
  const monitoringGps = stats?.monitoring_gps ?? [];

  const plantationPct = totalPlantsTarget > 0 ? ((totalTreesMonitored / totalPlantsTarget) * 100).toFixed(1) : 0;

  const milestoneLabels = { land_preparation: 'Land Preparation', plantation_progress: 'Plantation Progress', plantation_done: 'Plantation Done', survival_reporting: 'Survival Reporting' };

  const milestoneKeys = Object.keys(fundByMilestone);

  const statusMap = {
    published: { label: 'Published', color: '#2196f3', bg: '#e3f2fd' },
    in_progress: { label: 'In Progress', color: '#4caf50', bg: '#e8f5e9' },
    completed: { label: 'Completed', color: '#1a7431', bg: '#e8f5e9' },
  };

  return (
    <div>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1a7431 0%, #2e7d32 50%, #43a047 100%)',
        color: '#fff', padding: '28px 32px', borderRadius: '16px', marginBottom: '24px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-40px', right: '-20px', fontSize: '140px', opacity: 0.08, lineHeight: 1 }}>{'\uD83C\uDF0D'}</div>
        <h2 style={{ margin: '0 0 6px 0', fontSize: '26px', fontWeight: 700 }}>Admin Dashboard</h2>
        <p style={{ margin: 0, opacity: 0.9, fontSize: '14px', maxWidth: '600px' }}>
          Platform-wide plantation progress, stakeholder management, and moderation overview.
        </p>
        <p style={{ margin: '10px 0 0 0', opacity: 0.7, fontSize: '12px' }}>{today}</p>
      </div>

      {/* ===== COMPACT KPI STRIP ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { icon: '\uD83C\uDF33', value: totalTreesMonitored.toLocaleString(), label: 'Trees Monitored', sub: `${totalPlantsTarget.toLocaleString()} target`, color: '#1a7431' },
          { icon: '\uD83C\uDF3F', value: `${monitoringAvgSurvival}%`, label: 'Avg Survival Rate', sub: `${totalMonitoringEntries} entries`, color: monitoringAvgSurvival >= 75 ? '#2e7d32' : '#f57c00' },
          { icon: '\uD83D\uDCB0', value: `\u20B9${totalFundsDisbursed >= 100000 ? (totalFundsDisbursed/100000).toFixed(1) + 'L' : (totalFundsDisbursed/1000).toFixed(0) + 'K'}`, label: 'Funds Disbursed', sub: `\u20B9${totalFundsPending >= 100000 ? (totalFundsPending/100000).toFixed(1) + 'L' : (totalFundsPending/1000).toFixed(0) + 'K'} pending`, color: '#00897b' },
          { icon: '\uD83D\uDE4B', value: totalUsers, label: 'Users', sub: `${totalCsr} CSR, ${totalAgencies} Agency, ${totalIndividuals} Indiv`, color: '#1976d2' },
          { icon: '\uD83D\uDCCA', value: `${plantationPct}%`, label: 'Achievement', sub: `${totalTreesMonitored.toLocaleString()} of ${totalPlantsTarget.toLocaleString()}`, color: parseFloat(plantationPct) >= 50 ? '#2e7d32' : '#f57c00' },
        ].map(k => (
          <div key={k.label} style={{
            background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center',
            boxShadow: '0 1px 8px rgba(0,0,0,0.06)', borderTop: `3px solid ${k.color}`,
          }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>{k.icon}</div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginTop: '2px' }}>{k.label}</div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Monitoring & GPS Map (+ Monitoring History calendar) */}
      <MonitoringMapCard fetchSummary={adminAPI.getMonitoringSummary} />

      {/* ===== MODERATION QUEUE + PER-PROJECT PROGRESS ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.2fr', gap: '22px', marginBottom: '24px' }}>
        {/* Moderation Queue */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>Moderation Queue</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>Items awaiting your review</div>
          {[
            { label: 'User Approvals', value: pendingUsers, icon: '\uD83D\uDC65', color: '#1976d2', bg: '#e3f2fd', to: '/admin/users' },
            { label: 'Project Reviews', value: pendingTargets, icon: '\uD83C\uDFAF', color: '#7c4dff', bg: '#ede7f6', to: '/admin/targets' },
            { label: 'Report Reviews', value: pendingReports, icon: '\uD83D\uDCCB', color: '#e65100', bg: '#fff3e0', to: '/admin/reports' },
            { label: 'Monitoring Reviews', value: pendingGroupMonitoring, icon: '\uD83C\uDF33', color: '#2e7d32', bg: '#e8f5e9', to: '/admin/group-monitoring' },
            { label: 'Idea Moderation', value: pendingIdeas, icon: '\uD83D\uDCA1', color: '#ff8f00', bg: '#fff3e0', to: '/admin/ideas' },
            { label: 'Blog Moderation', value: pendingBlogPosts, icon: '\u270D\uFE0F', color: '#6a1b9a', bg: '#f3e5f5', to: '/admin/blog-moderation' },
          ].map(item => (
            <Link key={item.label} to={item.to} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', marginBottom: '6px', borderRadius: '10px', textDecoration: 'none', background: item.value > 0 ? item.bg : '#f9fafb', transition: 'all 0.2s' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: item.value > 0 ? item.color + '22' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>{item.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{item.label}</div>
              </div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: item.value > 0 ? item.color : '#d1d5db', minWidth: '30px', textAlign: 'right' }}>{item.value}</div>
            </Link>
          ))}
        </div>

        {/* Per-Project Progress Cards */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>Project-wise Progress</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>Plantation achievement across all projects</div>
          {projectProgress.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: projectProgress.length === 1 ? '1fr' : '1fr 1fr', gap: '14px' }}>
              {projectProgress.map((tp, idx) => {
                const tpCount = tp.monitored || tp.planted || 0;
                const plantPct = tp.total_plants > 0 ? Math.min((tpCount / tp.total_plants) * 100, 100) : 0;
                const st = statusMap[tp.status] || { label: tp.status, color: '#666', bg: '#f5f5f5' };
                return (
                  <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a2e', marginBottom: '3px', lineHeight: 1.3 }}>{tp.name}</div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', background: st.bg, color: st.color, fontWeight: 600 }}>{st.label}</span>
                          {tp.state && <span style={{ fontSize: '10px', color: '#9ca3af' }}>{tp.state}</span>}
                        </div>
                      </div>
                      <svg width="44" height="44" viewBox="0 0 44 44" style={{ flexShrink: 0 }}>
                        <circle cx="22" cy="22" r="18" fill="none" stroke="#f0f0f0" strokeWidth="3.5" />
                        <circle cx="22" cy="22" r="18" fill="none" stroke={plantPct >= 75 ? '#1a7431' : plantPct >= 40 ? '#ff9800' : '#ef5350'}
                          strokeWidth="3.5" strokeDasharray={`${plantPct * 1.131} 200`}
                          strokeLinecap="round" transform="rotate(-90 22 22)" />
                        <text x="22" y="22" textAnchor="middle" dominantBaseline="central"
                          style={{ fontSize: '10px', fontWeight: 700, fill: '#374151' }}>{plantPct.toFixed(0)}%</text>
                      </svg>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', fontSize: '11px', marginBottom: '8px' }}>
                      <span style={{ background: '#f9fafb', padding: '3px 6px', borderRadius: '4px' }}><strong style={{ color: '#1a7431' }}>{tpCount.toLocaleString()}</strong> monitored</span>
                      <span style={{ background: '#f9fafb', padding: '3px 6px', borderRadius: '4px' }}><strong style={{ color: '#1976d2' }}>{tp.agencies}</strong> agencies</span>
                      <span style={{ background: '#f9fafb', padding: '3px 6px', borderRadius: '4px' }}><strong style={{ color: '#e65100' }}>{tp.approved_reports}/{tp.reports}</strong> reports</span>
                    </div>
                    <div style={{ background: '#f3f4f6', borderRadius: '6px', height: '7px', overflow: 'hidden' }}>
                      <div style={{ width: `${plantPct}%`, height: '100%', background: plantPct >= 75 ? '#1a7431' : plantPct >= 40 ? '#ff9800' : '#ef5350', borderRadius: '6px', transition: 'width 0.8s' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af', marginTop: '3px' }}>
                      <span>{tpCount.toLocaleString()} / {tp.total_plants.toLocaleString()} trees</span>
                      <span>{plantPct.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '30px 0' }}>No active projects yet</div>
          )}
        </div>
      </div>

      {/* ===== FUND SUMMARY + STAKEHOLDER PIPELINE ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '22px', marginBottom: '24px' }}>
        {/* Fund Disbursement Summary */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>Fund Disbursement Summary</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>Milestone-wise fund utilisation</div>
          {/* Total Fund Bar */}
          {(() => {
            const totalFunds = totalFundsDisbursed + totalFundsPending;
            const disbPct = totalFunds > 0 ? (totalFundsDisbursed / totalFunds * 100) : 0;
            return (
              <div style={{ marginBottom: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Total: {'\u20B9'}{totalFunds >= 100000 ? (totalFunds/100000).toFixed(1) + 'L' : totalFunds.toLocaleString()}</span>
                  <span style={{ fontSize: '12px', color: '#1a7431', fontWeight: 600 }}>{disbPct.toFixed(0)}% utilised</span>
                </div>
                <div style={{ height: '10px', background: '#f3f4f6', borderRadius: '6px', overflow: 'hidden', display: 'flex' }}>
                  <div style={{ width: `${disbPct}%`, background: 'linear-gradient(90deg, #1a7431, #43a047)', borderRadius: '6px', transition: 'width 0.8s' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af', marginTop: '3px' }}>
                  <span>{'\u20B9'}{totalFundsDisbursed >= 100000 ? (totalFundsDisbursed/100000).toFixed(1) + 'L' : totalFundsDisbursed.toLocaleString()} disbursed</span>
                  <span>{'\u20B9'}{totalFundsPending >= 100000 ? (totalFundsPending/100000).toFixed(1) + 'L' : totalFundsPending.toLocaleString()} pending</span>
                </div>
              </div>
            );
          })()}
          {/* Per-Milestone Breakdown */}
          {milestoneKeys.length > 0 ? milestoneKeys.map(key => {
            const d = fundByMilestone[key];
            const total = (d?.disbursed || 0) + (d?.pending || 0);
            const pct = total > 0 ? ((d?.disbursed || 0) / total * 100) : 0;
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f8f9fa' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: pct >= 100 ? '#1a7431' : pct > 0 ? '#ff9800' : '#e0e0e0', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{milestoneLabels[key] || key}</div>
                  <div style={{ height: '5px', background: '#f3f4f6', borderRadius: '3px', overflow: 'hidden', marginTop: '4px' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? '#1a7431' : '#ff9800', borderRadius: '3px' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#1a7431' }}>{'\u20B9'}{(d?.disbursed || 0) >= 100000 ? ((d?.disbursed || 0)/100000).toFixed(1) + 'L' : (d?.disbursed || 0).toLocaleString()}</div>
                  <div style={{ fontSize: '10px', color: '#9ca3af' }}>{pct.toFixed(0)}%</div>
                </div>
              </div>
            );
          }) : (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px 0', fontSize: '13px' }}>No disbursements yet</div>
          )}
        </div>

        {/* Platform Overview */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>Platform Overview</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>Key metrics at a glance</div>
          <ProgressIndicator label="Plantation Achievement" value={totalTreesMonitored} max={totalPlantsTarget || 1} color="#1a7431" />
          {totalApplications > 0 && <ProgressIndicator label="Applications Accepted" value={acceptedApplications} max={totalApplications} color="#1976d2" />}
          <ProgressIndicator label="Projects Published" value={targetStatus.published || targetStatus.in_progress || 0} max={totalTargets || 1} color="#1976d2" />
          <ProgressIndicator label="Reports Approved" value={reportStatus.approved || 0} max={Object.values(reportStatus).reduce((a,b) => a+b, 0) || 1} color="#e65100" />
          <ProgressIndicator label="Funds Utilised" value={totalFundsDisbursed} max={(totalFundsDisbursed + totalFundsPending) || 1} color="#00897b" />

          {/* State-wise Summary */}
          {stateData.length > 0 && (
            <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '10px' }}>State-wise Projects</div>
              {stateData.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px' }}>{'\uD83D\uDCCD'}</span>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>{s.state}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                    <span style={{ color: '#1a7431', fontWeight: 600 }}>{s.plants?.toLocaleString()} trees</span>
                    <span style={{ color: '#6b7280' }}>{s.targets} {s.targets === 1 ? 'project' : 'projects'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '28px',
      }}>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '18px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
          Quick Actions
        </div>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          {[
            { to: '/admin/users', icon: '\uD83D\uDC65', label: `Review Users (${pendingUsers})`, bg: 'linear-gradient(135deg, #1a7431, #2e7d32)' },
            { to: '/admin/targets', icon: '\uD83C\uDFAF', label: `Moderate Projects (${pendingTargets})`, bg: 'linear-gradient(135deg, #1565c0, #1976d2)' },
            { to: '/admin/reports', icon: '\uD83D\uDCCB', label: `Review Reports (${pendingReports})`, bg: 'linear-gradient(135deg, #e65100, #f57c00)' },
            { to: '/admin/group-monitoring', icon: '\uD83C\uDF33', label: `Monitoring (${totalMonitoringEntries})`, bg: 'linear-gradient(135deg, #2e7d32, #43a047)' },
            { to: '/admin/disbursements', icon: '\uD83D\uDCB0', label: 'Disbursements', bg: 'linear-gradient(135deg, #00695c, #00897b)' },
            { to: '/admin/monitoring-progress', icon: '\uD83D\uDCCA', label: 'Monitoring Progress', bg: 'linear-gradient(135deg, #6a1b9a, #7c4dff)' },
          ].map(action => (
            <Link key={action.to} to={action.to} style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '12px 22px', borderRadius: '10px', background: action.bg,
              color: '#fff', textDecoration: 'none', fontSize: '14px', fontWeight: 500,
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}>
              {action.icon} {action.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// User Management
// ---------------------------------------------------------------------------
function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ role: '', status: '', search: '' });
  const [actionLoading, setActionLoading] = useState(null);

  const loadUsers = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: 15 };
    if (filters.role) params.role = filters.role;
    if (filters.status) params.status = filters.status;
    if (filters.search) params.search = filters.search;

    adminAPI.getUsers(params)
      .then(res => {
        setUsers(res.data.users || res.data.items || []);
        setPages(res.data.pages || 1);
      })
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [page, filters]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.approveUser(id);
      toast.success('User approved successfully');
      loadUsers();
    } catch { toast.error('Failed to approve user'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Rejection reason (optional):');
    setActionLoading(id);
    try {
      await adminAPI.rejectUser(id, { reason: reason || '' });
      toast.success('User rejected');
      loadUsers();
    } catch { toast.error('Failed to reject user'); }
    finally { setActionLoading(null); }
  };

  const handleSuspend = async (id) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    setActionLoading(id);
    try {
      await adminAPI.suspendUser(id);
      toast.success('User suspended');
      loadUsers();
    } catch { toast.error('Failed to suspend user'); }
    finally { setActionLoading(null); }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <div>
      <h3>User Management</h3>

      <div className="filter-bar">
        <select value={filters.role} onChange={e => handleFilterChange('role', e.target.value)}>
          <option value="">All Roles</option>
          <option value="csr">CSR</option>
          <option value="agency">Agency</option>
          <option value="individual">Individual</option>
          <option value="field_supervisor">Field Supervisor</option>
        </select>
        <select value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
        </select>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={filters.search}
          onChange={e => handleFilterChange('search', e.target.value)}
        />
      </div>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center' }}>No users found</td></tr>
              ) : (
                users.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.role}</td>
                    <td><StatusBadge status={user.status} /></td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td className="action-buttons">
                      {user.status === 'pending' && (
                        <>
                          <button
                            className="btn btn-success btn-sm"
                            disabled={actionLoading === user.id}
                            onClick={() => handleApprove(user.id)}
                          >Approve</button>
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={actionLoading === user.id}
                            onClick={() => handleReject(user.id)}
                          >Reject</button>
                        </>
                      )}
                      {user.status === 'approved' && (
                        <button
                          className="btn btn-warning btn-sm"
                          disabled={actionLoading === user.id}
                          onClick={() => handleSuspend(user.id)}
                        >Suspend</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending Targets
// ---------------------------------------------------------------------------
function TargetModeration() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewTarget, setViewTarget] = useState(null);

  const loadTargets = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: 15 };
    if (statusFilter) params.status = statusFilter;
    adminAPI.getAllTargets(params)
      .then(res => {
        setTargets(res.data.targets || res.data.items || []);
        setPages(res.data.pages || 1);
      })
      .catch(() => toast.error('Failed to load projects'))
      .finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => { loadTargets(); }, [loadTargets]);

  const handlePublish = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.publishTarget(id);
      toast.success('Project published successfully');
      loadTargets();
    } catch { toast.error('Failed to publish project'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this project?')) return;
    setActionLoading(id);
    try {
      await adminAPI.rejectTarget(id);
      toast.success('Project rejected');
      loadTargets();
    } catch { toast.error('Failed to reject project'); }
    finally { setActionLoading(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>All Plantation Projects</h3>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 }}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_review">Pending Review</option>
          <option value="published">Published</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>CSR Name</th>
                <th>Plants</th>
                <th>Budget</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {targets.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center' }}>No projects found</td></tr>
              ) : (
                targets.map(target => (
                  <tr key={target.id}>
                    <td>{target.title}</td>
                    <td>{target.csr_name || target.csr?.organisation_name || target.csr?.name || '-'}</td>
                    <td>{target.plant_count?.toLocaleString() ?? target.total_plants?.toLocaleString() ?? '-'}</td>
                    <td>{target.budget != null ? `\u20B9${Number(target.budget).toLocaleString()}` : '-'}</td>
                    <td><StatusBadge status={target.status} /></td>
                    <td className="action-buttons">
                      <button className="btn btn-sm" style={{ background: '#1976d2', color: '#fff' }}
                        onClick={() => setViewTarget(target)}>View</button>
                      <Link to={`/admin/targets/${target.id}`} className="btn btn-sm" style={{ background: '#2e7d32', color: '#fff', textDecoration: 'none' }}>Detail</Link>
                      {target.status === 'pending_review' && (
                        <>
                          <button className="btn btn-success btn-sm" disabled={actionLoading === target.id}
                            onClick={() => handlePublish(target.id)}>Publish</button>
                          <button className="btn btn-danger btn-sm" disabled={actionLoading === target.id}
                            onClick={() => handleReject(target.id)}>Reject</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}

      {/* View Target Modal */}
      {viewTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setViewTarget(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 700, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>{viewTarget.title}</h3>
              <button onClick={() => setViewTarget(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div><strong>Status:</strong> <StatusBadge status={viewTarget.status} /></div>
              <div><strong>Plants:</strong> {(viewTarget.total_plants || 0).toLocaleString()}</div>
              <div><strong>Budget:</strong> {viewTarget.budget ? `\u20B9${Number(viewTarget.budget).toLocaleString()}` : '-'}</div>
              <div><strong>Type:</strong> {viewTarget.plantation_type || '-'}</div>
              <div><strong>Location:</strong> {[viewTarget.city, viewTarget.state].filter(Boolean).join(', ') || '-'}</div>
              <div><strong>CSR:</strong> {viewTarget.csr_name || '-'}</div>
              <div><strong>Start:</strong> {viewTarget.start_date ? new Date(viewTarget.start_date).toLocaleDateString() : '-'}</div>
              <div><strong>End:</strong> {viewTarget.end_date ? new Date(viewTarget.end_date).toLocaleDateString() : '-'}</div>
            </div>
            {viewTarget.description && <div style={{ marginBottom: 12 }}><strong>Description:</strong><p style={{ margin: '4px 0', color: '#555' }}>{viewTarget.description}</p></div>}
            {viewTarget.species_details && <div><strong>Species:</strong><p style={{ margin: '4px 0', color: '#555' }}>{viewTarget.species_details}</p></div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending Reports
// ---------------------------------------------------------------------------
function ReportReview() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);

  const loadReports = useCallback(() => {
    setLoading(true);
    adminAPI.getPendingReports({ page, per_page: 15 })
      .then(res => {
        setReports(res.data.reports || res.data.items || []);
        setPages(res.data.pages || 1);
      })
      .catch(() => toast.error('Failed to load pending reports'))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.approveReport(id);
      toast.success('Report approved successfully');
      loadReports();
    } catch { toast.error('Failed to approve report'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Rejection reason (optional):');
    setActionLoading(id);
    try {
      await adminAPI.rejectReport(id, { reason: reason || '' });
      toast.success('Report rejected');
      loadReports();
    } catch { toast.error('Failed to reject report'); }
    finally { setActionLoading(null); }
  };

  return (
    <div>
      <h3>Report Review</h3>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Agency</th>
                <th>Project</th>
                <th>Type</th>
                <th>Plant Count</th>
                <th>Document</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center' }}>No pending reports</td></tr>
              ) : (
                reports.map(report => (
                  <tr key={report.id}>
                    <td>{report.agency_name || report.agency?.name || '-'}</td>
                    <td style={{ fontWeight: 500, maxWidth: 140 }}>{report.target_title || '-'}</td>
                    <td>{(report.report_type || report.type || '-').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                    <td>{report.plant_count?.toLocaleString() ?? '-'}</td>
                    <td>{report.report_document
                      ? <a href={report.report_document} target="_blank" rel="noopener noreferrer" style={{ color: '#2e7d32', fontWeight: 500, fontSize: 12 }}>📄 PDF</a>
                      : <span style={{ color: '#999', fontSize: 12 }}>—</span>
                    }</td>
                    <td>{new Date(report.created_at || report.submitted_at).toLocaleDateString()}</td>
                    <td className="action-buttons">
                      <button
                        className="btn btn-success btn-sm"
                        disabled={actionLoading === report.id}
                        onClick={() => handleApprove(report.id)}
                      >Approve</button>
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={actionLoading === report.id}
                        onClick={() => handleReject(report.id)}
                      >Reject</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending Ideas
// ---------------------------------------------------------------------------
function IdeaModeration() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);

  const loadIdeas = useCallback(() => {
    setLoading(true);
    adminAPI.getPendingIdeas({ page, per_page: 15 })
      .then(res => {
        setIdeas(res.data.ideas || res.data.items || []);
        setPages(res.data.pages || 1);
      })
      .catch(() => toast.error('Failed to load pending ideas'))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.approveIdea(id);
      toast.success('Idea approved successfully');
      loadIdeas();
    } catch { toast.error('Failed to approve idea'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this idea?')) return;
    setActionLoading(id);
    try {
      await adminAPI.rejectIdea(id);
      toast.success('Idea rejected');
      loadIdeas();
    } catch { toast.error('Failed to reject idea'); }
    finally { setActionLoading(null); }
  };

  const handleFeature = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.featureIdea(id);
      toast.success('Idea featured successfully');
      loadIdeas();
    } catch { toast.error('Failed to feature idea'); }
    finally { setActionLoading(null); }
  };

  return (
    <div>
      <h3>Idea Moderation</h3>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Poster</th>
                <th>Category</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ideas.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center' }}>No pending ideas</td></tr>
              ) : (
                ideas.map(idea => (
                  <tr key={idea.id}>
                    <td>{idea.title}</td>
                    <td>{idea.poster_name || idea.poster?.name || idea.user?.name || '-'}</td>
                    <td>{idea.category || '-'}</td>
                    <td>{new Date(idea.created_at).toLocaleDateString()}</td>
                    <td className="action-buttons">
                      <button
                        className="btn btn-success btn-sm"
                        disabled={actionLoading === idea.id}
                        onClick={() => handleApprove(idea.id)}
                      >Approve</button>
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={actionLoading === idea.id}
                        onClick={() => handleReject(idea.id)}
                      >Reject</button>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={actionLoading === idea.id}
                        onClick={() => handleFeature(idea.id)}
                      >Feature</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Disbursements
// ---------------------------------------------------------------------------
function Disbursements() {
  const [disbursements, setDisbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [rejectModal, setRejectModal] = useState(null); // stores disbursement id
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [newDisb, setNewDisb] = useState({ agency_user_id: '', allocation_id: '', milestone_type: 'land_preparation', amount: '', remarks: '' });
  const [editDisb, setEditDisb] = useState(null);

  const loadDisbursements = useCallback(() => {
    setLoading(true);
    adminAPI.getDisbursements({ page, per_page: 15 })
      .then(res => {
        setDisbursements(res.data.disbursements || res.data.items || []);
        setPages(res.data.pages || 1);
      })
      .catch(() => toast.error('Failed to load disbursements'))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { loadDisbursements(); }, [loadDisbursements]);

  // Load agencies for create form (deduplicated by organisation_name)
  useEffect(() => {
    if (showCreate && agencies.length === 0) {
      adminAPI.getUsers({ role: 'agency', status: 'approved', per_page: 100 })
        .then(res => {
          const all = res.data.users || res.data.items || [];
          const seen = {};
          const unique = all.filter(a => {
            const key = a.organisation_name || a.name;
            if (seen[key]) return false;
            seen[key] = true;
            return true;
          });
          setAgencies(unique);
        })
        .catch(() => {});
    }
  }, [showCreate, agencies.length]);

  const handleCreate = async () => {
    if (!newDisb.agency_user_id || !newDisb.amount || !newDisb.milestone_type) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await adminAPI.createDisbursement({
        agency_user_id: parseInt(newDisb.agency_user_id),
        allocation_id: newDisb.allocation_id ? parseInt(newDisb.allocation_id) : null,
        milestone_type: newDisb.milestone_type,
        amount: parseFloat(newDisb.amount),
        remarks: newDisb.remarks || '',
      });
      toast.success('Disbursement created');
      setShowCreate(false);
      setNewDisb({ agency_user_id: '', allocation_id: '', milestone_type: 'land_preparation', amount: '', remarks: '' });
      loadDisbursements();
    } catch { toast.error('Failed to create disbursement'); }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.approveDisbursement(id);
      toast.success('Disbursement approved');
      loadDisbursements();
    } catch { toast.error('Failed to approve disbursement'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(rejectModal);
    try {
      await adminAPI.rejectDisbursement(rejectModal, { remarks: rejectRemarks });
      toast.success('Disbursement rejected');
      setRejectModal(null);
      setRejectRemarks('');
      loadDisbursements();
    } catch { toast.error('Failed to reject disbursement'); }
    finally { setActionLoading(null); }
  };

  const handleDisburse = async (id) => {
    if (!window.confirm('Confirm fund disbursement?')) return;
    setActionLoading(id);
    try {
      await adminAPI.disburseFunds(id);
      toast.success('Funds disbursed successfully');
      loadDisbursements();
    } catch { toast.error('Failed to disburse funds'); }
    finally { setActionLoading(null); }
  };

  const handleEdit = async () => {
    if (!editDisb) return;
    const amt = parseFloat(String(editDisb.amount).replace(/,/g, ''));
    if (!amt || isNaN(amt)) { toast.error('Please enter a valid amount'); return; }
    try {
      await adminAPI.updateDisbursement(editDisb.id, {
        milestone_type: editDisb.milestone_type,
        amount: amt,
        remarks: editDisb.remarks || '',
        status: editDisb.status,
      });
      toast.success('Disbursement updated');
      setEditDisb(null);
      loadDisbursements();
    } catch { toast.error('Failed to update disbursement'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this disbursement? This cannot be undone.')) return;
    setActionLoading(id);
    try {
      await adminAPI.deleteDisbursement(id);
      toast.success('Disbursement deleted');
      loadDisbursements();
    } catch { toast.error('Failed to delete disbursement'); }
    finally { setActionLoading(null); }
  };

  const milestoneLabels = { land_preparation: 'Land Preparation', plantation_progress: 'Plantation Progress', plantation_done: 'Plantation Done', survival_reporting: 'Survival Reporting' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Fund Disbursements</h3>
        <button className="btn btn-success btn-sm" onClick={() => setShowCreate(true)}>+ Create Disbursement</button>
      </div>

      {/* Create Disbursement Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowCreate(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Create Disbursement</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Agency *</label>
                <select value={newDisb.agency_user_id} onChange={e => setNewDisb({ ...newDisb, agency_user_id: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
                  <option value="">Select Agency</option>
                  {agencies.map(a => <option key={a.id} value={a.id}>{a.organisation_name || a.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Milestone Type *</label>
                <select value={newDisb.milestone_type} onChange={e => setNewDisb({ ...newDisb, milestone_type: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
                  <option value="land_preparation">Land Preparation</option>
                  <option value="plantation_progress">Plantation Progress</option>
                  <option value="plantation_done">Plantation Done</option>
                  <option value="survival_reporting">Survival Reporting</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Amount (INR) *</label>
                <input type="number" value={newDisb.amount} onChange={e => setNewDisb({ ...newDisb, amount: e.target.value })}
                  placeholder="Enter amount" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Remarks</label>
                <textarea value={newDisb.remarks} onChange={e => setNewDisb({ ...newDisb, remarks: e.target.value })}
                  placeholder="Enter remarks (optional)" rows={3}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-sm" style={{ background: '#e5e7eb' }} onClick={() => setShowCreate(false)}>Cancel</button>
                <button className="btn btn-success btn-sm" onClick={handleCreate}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Disbursement Modal */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setRejectModal(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 450, width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#ef5350' }}>Reject Disbursement</h3>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Remarks / Reason for Rejection</label>
              <textarea value={rejectRemarks} onChange={e => setRejectRemarks(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-sm" style={{ background: '#e5e7eb' }} onClick={() => setRejectModal(null)}>Cancel</button>
              <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }} onClick={handleReject}
                disabled={actionLoading}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Disbursement Modal */}
      {editDisb && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setEditDisb(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Edit Disbursement</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Milestone Type *</label>
                <select value={editDisb.milestone_type} onChange={e => setEditDisb({ ...editDisb, milestone_type: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
                  <option value="land_preparation">Land Preparation</option>
                  <option value="plantation_progress">Plantation Progress</option>
                  <option value="plantation_done">Plantation Done</option>
                  <option value="survival_reporting">Survival Reporting</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Amount (INR) *</label>
                <input type="number" value={editDisb.amount} onChange={e => setEditDisb({ ...editDisb, amount: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Status</label>
                <select value={editDisb.status} onChange={e => setEditDisb({ ...editDisb, status: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="disbursed">Disbursed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>Remarks</label>
                <textarea value={editDisb.remarks || ''} onChange={e => setEditDisb({ ...editDisb, remarks: e.target.value })}
                  rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-sm" style={{ background: '#e5e7eb' }} onClick={() => setEditDisb(null)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleEdit}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Agency</th>
                <th>Milestone</th>
                <th>Amount</th>
                <th>Remarks</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {disbursements.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#999' }}>No disbursements yet. Click "Create Disbursement" to add one.</td></tr>
              ) : (
                disbursements.map(d => (
                  <tr key={d.id}>
                    <td>{d.agency_name || '-'}</td>
                    <td>{milestoneLabels[d.milestone_type] || d.milestone_type || '-'}</td>
                    <td style={{ fontWeight: 600 }}>{d.amount != null ? `\u20B9${Number(d.amount).toLocaleString()}` : '-'}</td>
                    <td style={{ fontSize: 13, color: '#555', maxWidth: 200 }}>{d.remarks || '-'}</td>
                    <td><StatusBadge status={d.status} /></td>
                    <td>{d.created_at ? new Date(d.created_at).toLocaleDateString() : '-'}</td>
                    <td className="action-buttons">
                      <button className="btn btn-sm" style={{ background: '#f3f4f6', marginRight: 4 }}
                        onClick={() => setEditDisb({ id: d.id, milestone_type: d.milestone_type, amount: d.amount, remarks: d.remarks || '', status: d.status })}>Edit</button>
                      <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff', marginRight: 4 }}
                        disabled={actionLoading === d.id}
                        onClick={() => handleDelete(d.id)}>Delete</button>
                      {(d.status === 'pending' || d.status === 'pending_approval') && (
                        <>
                          <button className="btn btn-success btn-sm" disabled={actionLoading === d.id}
                            onClick={() => handleApprove(d.id)}>Approve</button>
                          <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff', marginLeft: 4 }}
                            disabled={actionLoading === d.id}
                            onClick={() => { setRejectModal(d.id); setRejectRemarks(''); }}>Reject</button>
                        </>
                      )}
                      {d.status === 'approved' && (
                        <button className="btn btn-primary btn-sm" disabled={actionLoading === d.id}
                          onClick={() => handleDisburse(d.id)}>Disburse</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Field Supervisors
// ---------------------------------------------------------------------------
function AdminFieldSupervisors() {
  const [supervisors, setSupervisors] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyProjects, setAgencyProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', agency_user_id: '', employee_id: '', allocation_ids: [] });

  const load = useCallback(() => {
    setLoading(true);
    adminAPI.getFieldSupervisors()
      .then(res => setSupervisors(res.data.supervisors || res.data.items || []))
      .catch(() => toast.error('Failed to load field supervisors'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (showCreate && agencies.length === 0) {
      adminAPI.getAgencies()
        .then(res => setAgencies(res.data.agencies || res.data.items || []))
        .catch(() => {});
    }
  }, [showCreate, agencies.length]);

  useEffect(() => {
    if (!form.agency_user_id) { setAgencyProjects([]); return; }
    adminAPI.getAllocations({ agency_user_id: form.agency_user_id })
      .then(res => setAgencyProjects(res.data.items || []))
      .catch(() => setAgencyProjects([]));
  }, [form.agency_user_id]);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password || !form.agency_user_id) {
      toast.error('Name, email, password and agency are required');
      return;
    }
    try {
      await adminAPI.createFieldSupervisor({
        name: form.name, email: form.email, password: form.password,
        phone: form.phone || undefined, agency_user_id: parseInt(form.agency_user_id),
        employee_id: form.employee_id || undefined,
        allocation_ids: form.allocation_ids,
      });
      toast.success('Field supervisor created');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', phone: '', agency_user_id: '', employee_id: '', allocation_ids: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to create'); }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Field Supervisors</h3>
        <button className="btn btn-success btn-sm" onClick={() => setShowCreate(true)}>+ Add Field Supervisor</button>
      </div>

      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowCreate(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Create Field Supervisor</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Agency *</label>
                <select value={form.agency_user_id}
                  onChange={e => setForm({ ...form, agency_user_id: e.target.value, allocation_ids: [] })}
                  style={inputStyle}>
                  <option value="">Select Agency</option>
                  {agencies.map(a => <option key={a.id} value={a.id}>{a.organisation_name || a.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
              </div>
              <div>
                <label style={labelStyle}>Email *</label>
                <input style={inputStyle} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email address" />
              </div>
              <div>
                <label style={labelStyle}>Password *</label>
                <input style={inputStyle} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Set login password" />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" />
              </div>
              <div>
                <label style={labelStyle}>Employee ID</label>
                <input style={inputStyle} value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} placeholder="Optional employee ID" />
              </div>
              {form.agency_user_id && (
                <div>
                  <label style={labelStyle}>Assign Projects</label>
                  <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px' }}>
                    {agencyProjects.length === 0 ? (
                      <div style={{ color: '#999', fontSize: 13 }}>No projects for this agency</div>
                    ) : agencyProjects.map(p => {
                      const label = p.target?.title || `Project #${p.id}`;
                      return (
                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
                          <input type="checkbox" checked={form.allocation_ids.includes(p.id)}
                            onChange={() => setForm(prev => ({
                              ...prev,
                              allocation_ids: prev.allocation_ids.includes(p.id)
                                ? prev.allocation_ids.filter(x => x !== p.id)
                                : [...prev.allocation_ids, p.id]
                            }))} />
                          {label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-sm" style={{ background: '#e5e7eb' }} onClick={() => setShowCreate(false)}>Cancel</button>
                <button className="btn btn-success btn-sm" onClick={handleCreate}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="loading-spinner">Loading...</div> : (
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Phone</th><th>Agency</th><th>Employee ID</th><th>Assigned Projects</th><th>Status</th><th>Created</th></tr>
          </thead>
          <tbody>
            {supervisors.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: 40, color: '#999' }}>No field supervisors yet.</td></tr>
            ) : supervisors.map(u => {
              const assignedProjects = u.field_supervisor_profile?.assigned_projects || [];
              return (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.phone || '-'}</td>
                  <td>{u.field_supervisor_profile?.agency_name || '-'}</td>
                  <td>{u.field_supervisor_profile?.employee_id || '-'}</td>
                  <td style={{ fontSize: 12, maxWidth: 200 }}>
                    {assignedProjects.length === 0
                      ? <span style={{ color: '#999' }}>None</span>
                      : assignedProjects.map(p => p.title).join(', ')}
                  </td>
                  <td><StatusBadge status={u.status} /></td>
                  <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blog Management
// ---------------------------------------------------------------------------
const BLOG_CATEGORIES = ['Tree Knowledge', 'Heritage', 'CSR Impact', 'Technology', 'Environment', 'Community', 'Green Offerings'];
const INITIAL_BLOG_FORM = { title: '', summary: '', content: '', category: '', tags: '', read_time_min: 5, featured_image: '', status: 'draft' };

function BlogEditorModal({ open, onClose, onSaved, editPost }) {
  const [form, setForm] = useState(INITIAL_BLOG_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editPost) {
      setForm({
        title: editPost.title || '',
        summary: editPost.summary || '',
        content: editPost.content || '',
        category: editPost.category || '',
        tags: editPost.tags || '',
        read_time_min: editPost.read_time_min || 5,
        featured_image: editPost.featured_image || '',
        status: editPost.status || 'draft',
      });
    } else {
      setForm(INITIAL_BLOG_FORM);
    }
  }, [editPost]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'read_time_min' ? parseInt(value) || 5 : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.warn('Title and content are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editPost) {
        await adminAPI.updateBlog(editPost.id, form);
        toast.success('Blog post updated');
      } else {
        await adminAPI.createBlog(form);
        toast.success('Blog post created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save blog post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700 }}>
        <div className="modal-header">
          <h3>{editPost ? 'Edit Blog Post' : 'Create Blog Post'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label>Title *</label>
              <input type="text" name="title" value={form.title} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Summary</label>
              <textarea name="summary" value={form.summary} onChange={handleChange} rows={2} placeholder="Brief summary for listing pages" />
            </div>
            <div className="form-group">
              <label>Content *</label>
              <textarea name="content" value={form.content} onChange={handleChange} rows={10} placeholder="Full blog post content..." required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>Category</label>
                <select name="category" value={form.category} onChange={handleChange}>
                  <option value="">Select category</option>
                  {BLOG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={form.status} onChange={handleChange}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>Read Time (minutes)</label>
                <input type="number" name="read_time_min" value={form.read_time_min} onChange={handleChange} min={1} max={60} />
              </div>
              <div className="form-group">
                <label>Tags</label>
                <input type="text" name="tags" value={form.tags} onChange={handleChange} placeholder="comma-separated tags" />
              </div>
            </div>
            <div className="form-group">
              <label>Featured Image URL</label>
              <input type="text" name="featured_image" value={form.featured_image} onChange={handleChange} placeholder="https://..." />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : editPost ? 'Update Post' : 'Create Post'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function BlogManagement() {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (filterStatus) params.status = filterStatus;
      const res = await adminAPI.getBlogs(params);
      setPosts(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.publishBlog(id);
      toast.success('Post published');
      load();
    } catch { toast.error('Failed to publish'); }
    finally { setActionLoading(null); }
  };

  const handleArchive = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.archiveBlog(id);
      toast.success('Post archived');
      load();
    } catch { toast.error('Failed to archive'); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post permanently?')) return;
    setActionLoading(id);
    try {
      await adminAPI.deleteBlog(id);
      toast.success('Post deleted');
      load();
    } catch { toast.error('Failed to delete'); }
    finally { setActionLoading(null); }
  };

  const openEdit = async (post) => {
    try {
      const res = await adminAPI.getBlog(post.id);
      setEditPost(res.data.post);
      setShowModal(true);
    } catch {
      setEditPost(post);
      setShowModal(true);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h3>Blog Management</h3>
        <button className="btn btn-primary" onClick={() => { setEditPost(null); setShowModal(true); }}>+ New Blog Post</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['', 'draft', 'published', 'archived'].map(s => (
          <button key={s} onClick={() => { setFilterStatus(s); setPage(1); }}
            className={`btn btn-sm ${filterStatus === s ? 'btn-primary' : 'btn-secondary'}`}>
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <BlogEditorModal open={showModal} onClose={() => { setShowModal(false); setEditPost(null); }} onSaved={() => { setPage(1); load(); }} editPost={editPost} />

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : posts.length === 0 ? (
        <div className="empty-state">No blog posts found.</div>
      ) : (
        <>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <tr key={post.id}>
                    <td><strong>{post.title}</strong></td>
                    <td>{post.category || '-'}</td>
                    <td><StatusBadge status={post.status} /></td>
                    <td>{post.published_at ? new Date(post.published_at).toLocaleDateString() : '-'}</td>
                    <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(post)}>Edit</button>
                      {post.status !== 'published' && (
                        <button className="btn btn-sm btn-primary" disabled={actionLoading === post.id} onClick={() => handlePublish(post.id)}>Publish</button>
                      )}
                      {post.status === 'published' && (
                        <button className="btn btn-sm btn-secondary" disabled={actionLoading === post.id} onClick={() => handleArchive(post.id)}>Archive</button>
                      )}
                      <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }} disabled={actionLoading === post.id} onClick={() => handleDelete(post.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group Monitoring Review – Admin approval/rejection
// ---------------------------------------------------------------------------
function GroupMonitoringReview() {
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await adminAPI.getPendingGroupMonitoring({ page: p, per_page: 15, status: statusFilter || undefined });
      setEntries(res.data.items || res.data.entries || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load group monitoring entries');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(page); }, [page, load]);

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); setExpandedEntry(null); return; }
    try {
      const res = await adminAPI.getGroupMonitoringDetail(id);
      setExpandedEntry(res.data.entry || res.data);
      setExpandedId(id);
    } catch {
      toast.error('Failed to load entry details');
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.approveGroupMonitoring(id);
      toast.success('Entry approved!');
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectNotes.trim()) { toast.warn('Please provide rejection notes'); return; }
    setActionLoading(showRejectModal);
    try {
      await adminAPI.rejectGroupMonitoring(showRejectModal, { review_notes: rejectNotes });
      toast.success('Entry rejected');
      setShowRejectModal(null);
      setRejectNotes('');
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const healthColor = (health) => {
    const map = { excellent: '#059669', good: '#10b981', fair: '#f59e0b', poor: '#ef4444', critical: '#dc2626' };
    return map[health] || '#6b7280';
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;

  return (
    <div>
      <h3>Group Monitoring Review</h3>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['submitted', '', 'approved', 'rejected'].map((s) => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
              border: statusFilter === s ? '2px solid #166534' : '1px solid #d1d5db',
              background: statusFilter === s ? '#166534' : '#fff',
              color: statusFilter === s ? '#fff' : '#374151', fontWeight: statusFilter === s ? 600 : 400,
            }}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>

      {entries.length === 0 ? (
        <div className="empty-state">No group monitoring entries to review.</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th></th>
                  <th>Agency</th>
                  <th>Zone / Group</th>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Trees</th>
                  <th>Survival</th>
                  <th>Health</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <tr onClick={() => toggleExpand(entry.id)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontSize: 12 }}>{expandedId === entry.id ? '▼' : '▶'}</td>
                      <td><strong>{entry.agency_name || '-'}</strong></td>
                      <td>{entry.zone_name || 'Ad-hoc'}</td>
                      <td>{entry.target_title || `#${entry.target_id}`}</td>
                      <td>{entry.monitoring_date ? new Date(entry.monitoring_date).toLocaleDateString() : '-'}</td>
                      <td>
                        <div style={{ fontSize: 12 }}>
                          {entry.total_trees_monitored} total
                          <br />
                          <span style={{ color: '#059669' }}>✓{entry.trees_healthy || 0}</span>{' '}
                          <span style={{ color: '#f59e0b' }}>⚠{entry.trees_needs_attention || 0}</span>{' '}
                          <span style={{ color: '#ef4444' }}>✗{entry.trees_critical || 0}</span>{' '}
                          <span style={{ color: '#6b7280' }}>☠{entry.trees_dead || 0}</span>
                        </div>
                      </td>
                      <td>{entry.survival_rate != null ? `${entry.survival_rate}%` : '-'}</td>
                      <td>
                        <span style={{ color: healthColor(entry.overall_health), fontWeight: 600, textTransform: 'capitalize', fontSize: 13 }}>
                          {entry.overall_health || '-'}
                        </span>
                      </td>
                      <td><StatusBadge status={entry.status} /></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {entry.status === 'submitted' && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-sm btn-primary" disabled={actionLoading === entry.id} onClick={() => handleApprove(entry.id)}>
                              Approve
                            </button>
                            <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }} disabled={actionLoading === entry.id} onClick={() => { setShowRejectModal(entry.id); setRejectNotes(''); }}>
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* Expanded detail */}
                    {expandedId === entry.id && expandedEntry && (
                      <tr>
                        <td colSpan={10} style={{ background: '#f9fafb', padding: 20 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                            <div>
                              <h5 style={{ margin: '0 0 10px' }}>Entry Details</h5>
                              <div style={{ fontSize: 14, lineHeight: 2 }}>
                                <div><strong>Agency:</strong> {expandedEntry.agency_name || '-'}</div>
                                <div><strong>Zone:</strong> {expandedEntry.zone_name || 'Ad-hoc'}</div>
                                <div><strong>Notes:</strong> {expandedEntry.notes || 'None'}</div>
                                <div><strong>GPS:</strong> {expandedEntry.latitude && expandedEntry.longitude ? `${expandedEntry.latitude}, ${expandedEntry.longitude}` : 'Not recorded'}</div>
                                <div><strong>Samples:</strong> {expandedEntry.samples?.length || 0}</div>
                                <div><strong>Photos:</strong> {expandedEntry.photos?.length || 0}</div>
                                {expandedEntry.review_notes && <div><strong>Review Notes:</strong> {expandedEntry.review_notes}</div>}
                              </div>
                            </div>
                            <div>
                              <h5 style={{ margin: '0 0 10px' }}>Samples</h5>
                              {expandedEntry.samples && expandedEntry.samples.length > 0 ? (
                                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                                  <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>#</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Tree ID</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Height</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Girth</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Health</th>
                                  </tr></thead>
                                  <tbody>
                                    {expandedEntry.samples.map((s, i) => (
                                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '4px 8px' }}>{s.sample_number}</td>
                                        <td style={{ padding: '4px 8px' }}>{s.tree_identifier || '-'}</td>
                                        <td style={{ padding: '4px 8px' }}>{s.height_cm ? `${s.height_cm}cm` : '-'}</td>
                                        <td style={{ padding: '4px 8px' }}>{s.girth_cm ? `${s.girth_cm}cm` : '-'}</td>
                                        <td style={{ padding: '4px 8px', textTransform: 'capitalize' }}>{s.health_status?.replace(/_/g, ' ')}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : <p style={{ color: '#9ca3af', fontSize: 13 }}>No samples.</p>}
                            </div>
                          </div>
                          {expandedEntry.photos && expandedEntry.photos.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                              <h5 style={{ margin: '0 0 10px' }}>Photos ({expandedEntry.photos.length})</h5>
                              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {expandedEntry.photos.map((p, i) => (
                                  <div key={i} style={{ textAlign: 'center' }}>
                                    <a href={getPhotoUrl(p.photo_url)} target="_blank" rel="noopener noreferrer">
                                      <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type} style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                    </a>
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, textTransform: 'capitalize' }}>
                                      {p.photo_type}{p.latitude ? ` 📍` : ''}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Reject Group Monitoring Entry</h4>
              <button className="modal-close" onClick={() => setShowRejectModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Rejection Notes *</label>
                <textarea rows={4} value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="Explain why this entry is being rejected..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRejectModal(null)}>Cancel</button>
              <button className="btn" style={{ background: '#ef5350', color: '#fff' }} disabled={actionLoading} onClick={handleReject}>
                {actionLoading ? 'Rejecting...' : 'Reject Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Monitoring Progress (Admin - Overview Dashboard)
// ---------------------------------------------------------------------------
function AdminMonitoringProgress() {
  const HISTORY_PER_PAGE = 5;
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [editingZone, setEditingZone] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', tree_count: '', description: '' });
  const [historyPage, setHistoryPage] = useState(1);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxEntry, setLightboxEntry] = useState('');

  useEffect(() => { setHistoryPage(1); }, [selectedTargetId, selectedZoneId]);

  useEffect(() => {
    adminAPI.getMonitoringSummary()
      .then(res => setSummary(res.data))
      .catch(() => toast.error('Failed to load monitoring summary'))
      .finally(() => setLoading(false));
  }, []);

  const reload = () => { setLoading(true); adminAPI.getMonitoringSummary().then(res => setSummary(res.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false)); };

  if (loading) return <div className="loading-spinner">Loading monitoring data...</div>;

  const allZones = summary.monitoring_zones || [];
  const allEntries = summary.monitoring_entries || [];
  const targetsInfo = summary.targets_info || [];

  // Build target name lookup
  const targetNameMap = {};
  targetsInfo.forEach(t => { targetNameMap[t.id] = t.title; });

  // Filter by selected project
  const zones = selectedTargetId ? allZones.filter(z => z.target_id === parseInt(selectedTargetId)) : allZones;
  const entries = selectedTargetId ? allEntries.filter(e => e.target_id === parseInt(selectedTargetId)) : allEntries;

  // Approved-only, dedupe to ONE latest entry per zone (newest date wins, ties broken by highest id)
  const approvedEntries = entries.filter(e => e.status === 'approved');
  const _byZone = {};
  approvedEntries.forEach(e => {
    if (!e.zone_id || !e.monitoring_date) return;
    const cur = _byZone[e.zone_id];
    if (!cur || e.monitoring_date > cur.monitoring_date ||
        (e.monitoring_date === cur.monitoring_date && (e.id || 0) > (cur.id || 0))) {
      _byZone[e.zone_id] = e;
    }
  });
  const latestEntries = [...Object.values(_byZone), ...approvedEntries.filter(e => !e.zone_id)];
  const fHealthy = latestEntries.reduce((s, e) => s + (e.trees_healthy || 0), 0);
  const fAttention = latestEntries.reduce((s, e) => s + (e.trees_needs_attention || 0), 0);
  const fCritical = latestEntries.reduce((s, e) => s + (e.trees_critical || 0), 0);
  const fDead = latestEntries.reduce((s, e) => s + (e.trees_dead || 0), 0);
  // Trees Monitored = reporter-entered total (may exceed sum of health buckets if some trees are unclassified)
  const fTotalMonitored = latestEntries.reduce((s, e) => s + (e.total_trees_monitored || 0), 0);
  // Survival = (Healthy + Need Attention + Critical) / target planted total. Target = sum of total_plants for selected projects.
  const filteredTargets = selectedTargetId
    ? targetsInfo.filter(t => t.id === parseInt(selectedTargetId))
    : targetsInfo;
  const fTargetTotal = filteredTargets.reduce((s, t) => s + (t.total_plants || 0), 0);
  const fAliveTrees = fHealthy + fAttention + fCritical;
  const fAvgSurvival = fTargetTotal > 0 ? ((fAliveTrees / fTargetTotal) * 100).toFixed(2) : 0;
  const fAgencies = new Set(entries.map(e => e.agency_user_id)).size;
  const fTargets = new Set(entries.map(e => e.target_id)).size;
  const fPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);

  // Per-zone tree count from the latest approved entry
  const zoneTreeCount = {};
  latestEntries.forEach(e => {
    if (e.zone_id) zoneTreeCount[e.zone_id] = e.total_trees_monitored || 0;
  });

  const statusColors = { pending: '#ff9800', approved: '#4caf50', disbursed: '#2196f3', submitted: '#ff9800', rejected: '#ef5350', under_review: '#ff9800', draft: '#9e9e9e' };
  const healthColors = { excellent: '#1b5e20', good: '#4caf50', fair: '#ff9800', poor: '#f44336', critical: '#b71c1c' };
  const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
  const zoneColorMap = {};
  zones.forEach((z, i) => { zoneColorMap[z.id] = zoneColors[i % zoneColors.length]; });

  const fmt = n => (n || 0).toLocaleString();
  const pct = (v, max) => max > 0 ? `${((v / max) * 100).toFixed(1)}%` : '0%';

  // Collect map points
  const allPoints = [];
  zones.forEach(z => {
    if (z.boundary_polygon) z.boundary_polygon.forEach(p => allPoints.push([p.lat, p.lng]));
    if (z.center_latitude && z.center_longitude) allPoints.push([z.center_latitude, z.center_longitude]);
  });
  entries.forEach(e => {
    if (e.latitude && e.longitude) allPoints.push([e.latitude, e.longitude]);
    (e.photos || []).forEach(p => { if (p.latitude && p.longitude) allPoints.push([p.latitude, p.longitude]); });
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0, color: '#1b5e20' }}>Monitoring Progress</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="admin-project-filter" style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Project:</label>
          <select id="admin-project-filter" value={selectedTargetId} onChange={e => { setSelectedTargetId(e.target.value); setSelectedZoneId(null); }}
            style={{ padding: '10px 16px', borderRadius: 10, border: '2px solid #1b5e20', background: '#fff', fontSize: 14, minWidth: 260, fontWeight: 500, color: '#1b5e20', cursor: 'pointer' }}>
            <option value="">All Projects ({targetsInfo.length})</option>
            {targetsInfo.map(t => (
              <option key={t.id} value={t.id}>{t.title} — {[t.city, t.state].filter(Boolean).join(', ')}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Trees Monitored', value: fmt(fTotalMonitored), sub: `${fTargets} targets`, color: '#4caf50', icon: '\uD83C\uDF33' },
          { label: 'Avg Survival Rate', value: `${fAvgSurvival}%`, sub: `${entries.length} entries`, color: fAvgSurvival >= 80 ? '#4caf50' : '#ff9800', icon: '\uD83D\uDCC8' },
          { label: 'Agencies Reporting', value: fmt(fAgencies), sub: `${fmt(fPhotos)} photos`, color: '#1976d2', icon: '\uD83C\uDFE2' },
          { label: 'Trees Healthy', value: fmt(fHealthy), sub: pct(fHealthy, fTotalMonitored) + ' of monitored', color: '#2e7d32', icon: '\u2705' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: '#e8f5e9', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{kpi.icon} {kpi.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1b5e20' }}>{kpi.value}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Tree Health Summary */}
      <div className="card" style={{ padding: 28, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Tree Health Summary</h3>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
          <span>Healthy: <strong style={{ color: '#4caf50' }}>{fmt(fHealthy)}</strong></span>
          <span>Needs Attention: <strong style={{ color: '#ff9800' }}>{fmt(fAttention)}</strong></span>
          <span>Critical: <strong style={{ color: '#f44336' }}>{fmt(fCritical)}</strong></span>
          <span>Dead: <strong style={{ color: '#999' }}>{fmt(fDead)}</strong></span>
          <span>Total: <strong>{fmt(fTotalMonitored)}</strong></span>
          {fTotalMonitored > 0 && <span>Survival: <strong style={{ color: '#1b5e20' }}>{fAvgSurvival}%</strong></span>}
        </div>
      </div>

      {/* GPS Map — zone polygons and photo markers */}
      {allPoints.length > 0 && (() => {
        const bounds = L.latLngBounds(allPoints);
        return (
          <div className="card" style={{ padding: 28, marginBottom: 24 }}>
            <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring & GPS Mapping</h3>
            <div style={{ height: 450, borderRadius: 12, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <MapContainer bounds={bounds.pad(0.1)} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Esri" />
                <ZoneFlyTo zones={zones} entries={entries} selectedZoneId={selectedZoneId} />
                {zones.map((z) => z.boundary_polygon && (
                  <Polygon key={z.id} positions={z.boundary_polygon.map(p => [p.lat, p.lng])}
                    pathOptions={{ color: zoneColorMap[z.id], weight: selectedZoneId === z.id ? 5 : 3, fillOpacity: selectedZoneId === z.id ? 0.35 : 0.2 }}>
                    <Popup><strong>{z.name}</strong><br />{(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees{z.area_sqm ? ` | ${Number(z.area_sqm).toLocaleString()} sq m` : ''}</Popup>
                  </Polygon>
                ))}
                {(() => {
                  const groupMap = new Map();
                  entries.forEach(e => {
                    (e.photos || []).forEach(p => {
                      if (!(p.latitude && p.longitude)) return;
                      const key = e.zone_id ? `zone-${e.zone_id}` : `entry-${e.id}`;
                      if (!groupMap.has(key)) {
                        groupMap.set(key, {
                          zoneId: e.zone_id || null,
                          label: e.zone_name || 'Monitoring Entry',
                          color: zoneColorMap[e.zone_id] || '#e65100',
                          photos: [],
                        });
                      }
                      groupMap.get(key).photos.push({
                        id: p.id,
                        url: getPhotoUrl(p.photo_url),
                        type: p.photo_type,
                        lat: Number(p.latitude),
                        lng: Number(p.longitude),
                        zoneName: e.zone_name,
                        entryDate: e.monitoring_date,
                      });
                    });
                  });
                  const groups = [];
                  groupMap.forEach((g, key) => {
                    if (g.photos.length === 0) return;
                    const cLat = g.photos.reduce((s, p) => s + p.lat, 0) / g.photos.length;
                    const cLng = g.photos.reduce((s, p) => s + p.lng, 0) / g.photos.length;
                    groups.push({ key, centroidLat: cLat, centroidLng: cLng, ...g });
                  });
                  return groups.map(g => {
                    const isSelected = selectedZoneId && g.zoneId === selectedZoneId;
                    const size = isSelected ? 34 : 26;
                    const count = g.photos.length;
                    return (
                      <Marker key={g.key} position={[g.centroidLat, g.centroidLng]}
                        icon={L.divIcon({
                          className: '',
                          html: `<div style="background:${g.color};color:#fff;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 0 6px rgba(0,0,0,0.5);font-size:${size >= 30 ? 13 : 11}px;font-weight:700;${isSelected ? 'animation:pulse 1s infinite;' : ''}">${count}</div>`,
                          iconSize: [size, size],
                          iconAnchor: [size/2, size/2],
                        })}>
                        <Popup maxWidth={320} minWidth={280}>
                          <PhotoCarousel
                            photos={g.photos}
                            label={g.label}
                            centroidLat={g.centroidLat}
                            centroidLng={g.centroidLng}
                          />
                        </Popup>
                      </Marker>
                    );
                  });
                })()}
              </MapContainer>
            </div>
            {/* Clickable zone legend */}
            <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
              <span onClick={() => setSelectedZoneId(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: !selectedZoneId ? '#e8f5e9' : '#fff', border: !selectedZoneId ? '2px solid #4caf50' : '1px solid #e0e0e0', fontWeight: !selectedZoneId ? 700 : 400 }}>
                All Zones
              </span>
              {zones.map((z) => (
                <span key={z.id} onClick={() => setSelectedZoneId(selectedZoneId === z.id ? null : z.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: selectedZoneId === z.id ? zoneColorMap[z.id] + '22' : '#fff', border: selectedZoneId === z.id ? `2px solid ${zoneColorMap[z.id]}` : '1px solid #e0e0e0', fontWeight: selectedZoneId === z.id ? 700 : 400 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: zoneColorMap[z.id], display: 'inline-block' }} />
                  {z.name} ({(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees)
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Monitoring Zone Cards */}
      {zones.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring Zones <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>— click a zone to see it on the map</span></h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {zones.map((z, i) => {
              const isActive = selectedZoneId === z.id;
              const zColor = zoneColors[i % zoneColors.length];
              return (
              <div key={z.id} onClick={() => { setSelectedZoneId(isActive ? null : z.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{ border: isActive ? `2px solid ${zColor}` : '1px solid #e0e0e0', borderRadius: 12, padding: 16, background: isActive ? zColor + '0a' : '#fafafa', cursor: 'pointer', transition: 'all 0.25s', transform: isActive ? 'scale(1.02)' : 'scale(1)', boxShadow: isActive ? `0 4px 16px ${zColor}33` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, background: zColor, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: '#1b5e20', fontSize: 15 }}>{z.name}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                    {isActive && <span style={{ fontSize: 11, background: zColor, color: '#fff', padding: '2px 8px', borderRadius: 10 }}>Viewing on map</span>}
                    <button onClick={ev => { ev.stopPropagation(); setEditingZone(z); setEditForm({ name: z.name, tree_count: z.tree_count || 0, description: z.description || '' }); }}
                      style={{ background: '#e3f2fd', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#1976d2', fontWeight: 600 }} title="Edit zone">✏️</button>
                    <button onClick={ev => { ev.stopPropagation(); if (window.confirm(`Delete "${z.name}" and all its monitoring entries?`)) { adminAPI.deleteZone(z.id).then(() => { toast.success('Zone deleted'); reload(); }).catch(() => toast.error('Failed to delete zone')); }}}
                      style={{ background: '#ffebee', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#d32f2f', fontWeight: 600 }} title="Delete zone">🗑️</button>
                  </span>
                </div>
                {z.description && <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{z.description}</div>}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                  <span>Trees: <strong>{(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()}</strong></span>
                  {z.area_sqm && <span>Area: <strong>{Number(z.area_sqm).toLocaleString()}</strong> sq m</span>}
                  <span style={{ textTransform: 'capitalize', color: '#888' }}>{z.zone_type?.replace('_', ' ')}</span>
                </div>
                {z.boundary_polygon ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#4caf50', fontWeight: 600 }}>GPS Boundary Mapped ({z.boundary_polygon.length} points)</div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#ff9800' }}>GPS boundary not mapped yet</div>
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>{z.entries_count || 0} monitoring {(z.entries_count || 0) === 1 ? 'entry' : 'entries'}</div>
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Zone Edit Modal */}
      {editingZone && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setEditingZone(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 420, maxWidth: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }} onClick={ev => ev.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', color: '#1b5e20' }}>Edit Zone</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Zone Name</label>
              <input value={editForm.name} onChange={ev => setEditForm({ ...editForm, name: ev.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Tree Count</label>
              <input type="number" value={editForm.tree_count} onChange={ev => setEditForm({ ...editForm, tree_count: ev.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Description</label>
              <textarea value={editForm.description} onChange={ev => setEditForm({ ...editForm, description: ev.target.value })} rows={3}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingZone(null)}
                style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={() => {
                  adminAPI.updateZone(editingZone.id, { name: editForm.name, tree_count: parseInt(editForm.tree_count) || 0, description: editForm.description })
                    .then(() => { toast.success('Zone updated'); setEditingZone(null); reload(); })
                    .catch(() => toast.error('Failed to update zone'));
                }}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#1b5e20', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Monitoring Entries with Photos */}
      {/* Summary stats — before history (planted count from API) */}
      {entries.length > 0 && (() => {
        const totalPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);
        return (
          <div style={{ display: 'flex', gap: 24, marginBottom: 20, padding: '12px 16px', background: '#f5f5f5', borderRadius: 10, fontSize: 13, flexWrap: 'wrap' }}>
            <span>Total Visits: <strong>{entries.length}</strong></span>
            <span>Trees Monitored: <strong>{fTotalMonitored.toLocaleString()}</strong></span>
            {fAvgSurvival > 0 && <span>Avg Survival: <strong>{fAvgSurvival}%</strong></span>}
            <span>Photos: <strong>{totalPhotos}</strong></span>
          </div>
        );
      })()}

      {entries.length > 0 && (() => {
        const historyPages = Math.max(1, Math.ceil(entries.length / HISTORY_PER_PAGE));
        const currentPage = Math.min(historyPage, historyPages);
        const pagedEntries = entries.slice((currentPage - 1) * HISTORY_PER_PAGE, currentPage * HISTORY_PER_PAGE);
        return (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, color: '#1b5e20' }}>Monitoring History</h3>
            <span style={{ fontSize: 12, color: '#888' }}>
              Showing {(currentPage - 1) * HISTORY_PER_PAGE + 1}&ndash;{Math.min(currentPage * HISTORY_PER_PAGE, entries.length)} of {entries.length}
            </span>
          </div>
          {pagedEntries.map(e => {
            const photos = e.photos || [];
            return (
              <div key={e.id} style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: 16, marginBottom: 16, background: '#fafffe', marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    {!selectedTargetId && targetNameMap[e.target_id] && (
                      <div style={{ fontSize: 11, color: '#1976d2', fontWeight: 600, marginBottom: 3, background: '#e3f2fd', display: 'inline-block', padding: '2px 8px', borderRadius: 6 }}>
                        {targetNameMap[e.target_id]}
                      </div>
                    )}
                    <div>
                      <strong style={{ fontSize: 14 }}>{e.zone_name || 'Unknown Zone'}</strong>
                      <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{e.monitoring_date ? new Date(e.monitoring_date).toLocaleDateString() : ''}</span>
                      {e.agency_name && <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>by {e.agency_name}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: healthColors[e.overall_health] || '#666', background: (healthColors[e.overall_health] || '#666') + '18', textTransform: 'capitalize' }}>{e.overall_health}</span>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: statusColors[e.status] || '#666', background: (statusColors[e.status] || '#666') + '18' }}>{e.status}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>Trees: <strong>{(e.total_trees_monitored || 0).toLocaleString()}</strong></span>
                  <span>Healthy: <strong style={{ color: '#4caf50' }}>{e.trees_healthy || 0}</strong></span>
                  <span>Attention: <strong style={{ color: '#ff9800' }}>{e.trees_needs_attention || 0}</strong></span>
                  <span>Critical: <strong style={{ color: '#f44336' }}>{e.trees_critical || 0}</strong></span>
                  <span>Dead: <strong style={{ color: '#999' }}>{e.trees_dead || 0}</strong></span>
                  {e.survival_rate != null && <span>Survival: <strong>{e.survival_rate}%</strong></span>}
                </div>
                {e.notes && <div style={{ marginTop: 8, fontSize: 13, color: '#555', fontStyle: 'italic' }}>{e.notes}</div>}
                {/* Entry GPS */}
                {e.latitude && e.longitude && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#1976d2' }}>
                    📍 Entry GPS: {Number(e.latitude).toFixed(6)}, {Number(e.longitude).toFixed(6)}
                  </div>
                )}
                {photos.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>Photos ({photos.length})</div>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                      {photos.map((p, pIdx) => (
                        <div key={p.id} onClick={() => { setLightboxPhotos(photos); setLightboxIndex(pIdx); setLightboxEntry(e.zone_name || ''); }}
                          style={{ flexShrink: 0, textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ position: 'relative', width: 100, height: 75 }}>
                            <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type}
                              style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8, border: '2px solid #e0e0e0' }}
                              onError={ev => { ev.target.style.display = 'none'; }} />
                            {p.latitude && p.longitude && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 8, padding: '2px 3px', borderRadius: '0 0 6px 6px', lineHeight: 1.2, textAlign: 'center' }}>
                                {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: '#888', marginTop: 2, textTransform: 'capitalize' }}>{p.photo_type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {historyPages > 1 && (
            <div style={{ marginTop: 16 }}>
              <Pagination page={currentPage} pages={historyPages} onPageChange={setHistoryPage} />
            </div>
          )}
        </div>
        );
      })()}

      {entries.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'\uD83D\uDCCA'}</div>
          <h3 style={{ color: '#1b5e20', margin: '0 0 8px' }}>No Monitoring Data Yet</h3>
          <p style={{ color: '#888' }}>Monitoring entries will appear here once agencies start submitting field reports.</p>
        </div>
      )}

      {/* Photo Lightbox */}
      {lightboxPhotos && (
        <PhotoLightbox photos={lightboxPhotos} initialIndex={lightboxIndex} entryInfo={lightboxEntry} onClose={() => setLightboxPhotos(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blog Moderation – Approve/Reject user-submitted blog posts
// ---------------------------------------------------------------------------
function BlogModeration() {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPendingBlogs({ page, per_page: 15 });
      setPosts(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load pending blog posts');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await adminAPI.approveBlog(id);
      toast.success('Blog post approved and published');
      load();
    } catch { toast.error('Failed to approve blog post'); }
    finally { setActionLoading(null); }
  };

  const openReject = (id) => {
    setShowRejectModal(id);
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.warn('Please provide a rejection reason');
      return;
    }
    setActionLoading(showRejectModal);
    try {
      await adminAPI.rejectBlog(showRejectModal, { reason: rejectReason });
      toast.success('Blog post rejected');
      setShowRejectModal(null);
      setRejectReason('');
      load();
    } catch { toast.error('Failed to reject blog post'); }
    finally { setActionLoading(null); }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div>
      <h3>Blog Moderation</h3>
      <p style={{ color: '#6b7280', marginBottom: 16 }}>Review and approve or reject user-submitted blog posts.</p>

      {/* Reject reason modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Reject Blog Post</h3>
              <button className="modal-close" onClick={() => setShowRejectModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  placeholder="Explain why this post is being rejected..."
                  style={{ width: '100%' }}
                />
              </div>
              <div className="form-actions">
                <button className="btn btn-secondary" onClick={() => setShowRejectModal(null)}>Cancel</button>
                <button className="btn" style={{ background: '#ef5350', color: '#fff' }}
                  disabled={actionLoading === showRejectModal}
                  onClick={handleReject}>
                  {actionLoading === showRejectModal ? 'Rejecting...' : 'Reject Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : posts.length === 0 ? (
        <div className="empty-state">No pending blog posts to review.</div>
      ) : (
        <>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Author</th>
                  <th>Role</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <React.Fragment key={post.id}>
                    <tr>
                      <td>{post.author_name || post.author?.name || '-'}</td>
                      <td>{post.author_role || post.author?.role || '-'}</td>
                      <td><strong>{post.title}</strong></td>
                      <td>{post.category || '-'}</td>
                      <td>{new Date(post.created_at).toLocaleDateString()}</td>
                      <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => toggleExpand(post.id)}>
                          {expandedId === post.id ? 'Hide' : 'View'}
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          disabled={actionLoading === post.id}
                          onClick={() => handleApprove(post.id)}
                        >Approve</button>
                        <button
                          className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }}
                          disabled={actionLoading === post.id}
                          onClick={() => openReject(post.id)}
                        >Reject</button>
                      </td>
                    </tr>
                    {expandedId === post.id && (
                      <tr>
                        <td colSpan="6" style={{ background: '#f9fafb', padding: '16px' }}>
                          {post.summary && (
                            <div style={{ marginBottom: '12px' }}>
                              <strong>Summary:</strong>
                              <p style={{ margin: '4px 0', color: '#374151' }}>{post.summary}</p>
                            </div>
                          )}
                          <div style={{ marginBottom: '12px' }}>
                            <strong>Content:</strong>
                            <div style={{ margin: '4px 0', color: '#374151', whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' }}>
                              {post.content}
                            </div>
                          </div>
                          {post.tags && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Tags:</strong> {post.tags}
                            </div>
                          )}
                          {post.featured_image && (
                            <div>
                              <strong>Featured Image:</strong>{' '}
                              <a href={post.featured_image} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>View Image</a>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Target Detail (Agency-style with map, zones, reports, disbursements)
// ---------------------------------------------------------------------------
function AdminTargetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxEntry, setLightboxEntry] = useState('');

  useEffect(() => {
    adminAPI.getTargetDetail(id)
      .then(res => setTarget(res.data.target || res.data))
      .catch(() => toast.error('Failed to load project details'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!target) return <div className="empty-state">Project not found.</div>;

  const milestoneLabels = { land_preparation: 'Land Preparation', plantation_progress: 'Plantation Progress', plantation_done: 'Plantation Done', survival_reporting: 'Survival Reporting' };
  const statusColors = { pending: '#ff9800', approved: '#4caf50', disbursed: '#2196f3', submitted: '#ff9800', rejected: '#ef5350', under_review: '#ff9800', failed: '#ef5350' };

  // Compute actual monitored tree count per zone from latest monitoring entries
  const _allEntries = target.monitoring_entries || [];
  const _zoneLatest = {};
  _allEntries.forEach(e => {
    if (e.zone_id && e.monitoring_date) {
      if (!_zoneLatest[e.zone_id] || e.monitoring_date > _zoneLatest[e.zone_id]) _zoneLatest[e.zone_id] = e.monitoring_date;
    }
  });
  const _latestEntries = _allEntries.filter(e => (e.zone_id && e.monitoring_date === _zoneLatest[e.zone_id]) || !e.zone_id);
  const zoneTreeCount = {};
  _latestEntries.forEach(e => {
    if (e.zone_id) {
      const total = (e.trees_healthy || 0) + (e.trees_needs_attention || 0) + (e.trees_critical || 0) + (e.trees_dead || 0);
      zoneTreeCount[e.zone_id] = (zoneTreeCount[e.zone_id] || 0) + total;
    }
  });

  return (
    <div>
      <button onClick={() => navigate('/admin/targets')}
        style={{ background: 'none', border: 'none', color: '#2e7d32', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 20, padding: 0 }}>
        &larr; Back to Projects
      </button>

      {/* Project Overview Card */}
      <div className="card" style={{ padding: 28, marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 6px', color: '#1b5e20' }}>{target.title || `Target #${target.id}`}</h2>
        <div style={{ fontSize: 14, color: '#888', marginBottom: 20 }}>
          {[target.city, target.state].filter(Boolean).join(', ')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          <div style={{ background: '#e8f5e9', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Total Plants</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1b5e20' }}>{(target.total_plants || 0).toLocaleString()}</div>
          </div>
          <div style={{ background: '#e3f2fd', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Budget</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1565c0' }}>{'\u20B9'}{Number(target.budget || 0).toLocaleString()}</div>
          </div>
          <div style={{ background: '#fff3e0', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Status</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#e65100' }}><StatusBadge status={target.status} /></div>
          </div>
          <div style={{ background: '#f3e5f5', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Plantation Type</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#6a1b9a' }}>{target.plantation_type || '-'}</div>
          </div>
        </div>
      </div>

      {target.description && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Description</h3>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{target.description}</p>
        </div>
      )}

      {/* Plant Species (admin can edit) */}
      <ProjectSpeciesSection targetId={target.id} adminMode={true} />

      {/* Milestones */}
      {target.milestones && target.milestones.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Milestones</h3>
          <table className="data-table">
            <thead><tr><th>Milestone</th><th>Due Date</th><th>Status</th></tr></thead>
            <tbody>
              {target.milestones.map(m => (
                <tr key={m.id}>
                  <td>{milestoneLabels[m.milestone_type] || m.milestone_type || m.title}</td>
                  <td>{m.due_date ? new Date(m.due_date).toLocaleDateString() : '-'}</td>
                  <td><StatusBadge status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Allocations */}
      {target.allocations && target.allocations.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Agency Allocations</h3>
          <table className="data-table">
            <thead><tr><th>Agency</th><th>Allocated Plants</th><th>Allocated Budget</th><th>Status</th></tr></thead>
            <tbody>
              {target.allocations.map(a => (
                <tr key={a.id}>
                  <td>{a.agency_name || `Agency #${a.agency_user_id}`}</td>
                  <td>{(a.allocated_plants || 0).toLocaleString()}</td>
                  <td>{'\u20B9'}{Number(a.allocated_budget || 0).toLocaleString()}</td>
                  <td><StatusBadge status={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reports */}
      {target.reports && target.reports.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Submitted Reports</h3>
          <table className="data-table">
            <thead><tr><th>Type</th><th>Agency</th><th>Plant Count</th><th>Survival Rate</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {target.reports.map(r => (
                <tr key={r.id}>
                  <td>{milestoneLabels[r.report_type] || r.report_type}</td>
                  <td>{r.agency_name || '-'}</td>
                  <td>{r.plant_count?.toLocaleString() || '-'}</td>
                  <td>{r.survival_rate != null ? `${r.survival_rate}%` : '-'}</td>
                  <td><span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: statusColors[r.status] || '#666', background: (statusColors[r.status] || '#666') + '18' }}>{r.status}</span></td>
                  <td>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Disbursements */}
      {target.disbursements && target.disbursements.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Fund Disbursements</h3>
          <table className="data-table">
            <thead><tr><th>Milestone</th><th>Agency</th><th>Amount</th><th>Remarks</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {target.disbursements.map(d => (
                <tr key={d.id}>
                  <td>{milestoneLabels[d.milestone_type] || d.milestone_type}</td>
                  <td>{d.agency_name || '-'}</td>
                  <td style={{ fontWeight: 600 }}>{'\u20B9'}{Number(d.amount).toLocaleString()}</td>
                  <td style={{ fontSize: 13, color: '#555' }}>{d.remarks || '-'}</td>
                  <td><span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: statusColors[d.status] || '#666', background: (statusColors[d.status] || '#666') + '18' }}>{d.status}</span></td>
                  <td>{d.created_at ? new Date(d.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* GPS Map — zone polygons and photo markers */}
      {(() => {
        const zones = target.monitoring_zones || [];
        const entries = target.monitoring_entries || [];
        const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
        const zoneColorMap = {};
        zones.forEach((z, i) => { zoneColorMap[z.id] = zoneColors[i % zoneColors.length]; });
        const allPoints = [];
        zones.forEach(z => {
          if (z.boundary_polygon) z.boundary_polygon.forEach(p => allPoints.push([p.lat, p.lng]));
          if (z.center_latitude && z.center_longitude) allPoints.push([z.center_latitude, z.center_longitude]);
        });
        entries.forEach(e => {
          if (e.latitude && e.longitude) allPoints.push([e.latitude, e.longitude]);
          (e.photos || []).forEach(p => { if (p.latitude && p.longitude) allPoints.push([p.latitude, p.longitude]); });
        });
        if (allPoints.length === 0) return null;
        const bounds = L.latLngBounds(allPoints);
        return (
          <div className="card" style={{ padding: 28, marginBottom: 24 }}>
            <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring & GPS Mapping</h3>
            <div style={{ height: 450, borderRadius: 12, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <MapContainer bounds={bounds.pad(0.1)} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Esri" />
                <ZoneFlyTo zones={zones} entries={entries} selectedZoneId={selectedZoneId} />
                {zones.map((z) => z.boundary_polygon && (
                  <Polygon key={z.id} positions={z.boundary_polygon.map(p => [p.lat, p.lng])}
                    pathOptions={{ color: zoneColorMap[z.id], weight: selectedZoneId === z.id ? 5 : 3, fillOpacity: selectedZoneId === z.id ? 0.35 : 0.2 }}>
                    <Popup><strong>{z.name}</strong><br />{(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees{z.area_sqm ? ` | ${Number(z.area_sqm).toLocaleString()} sq m` : ''}</Popup>
                  </Polygon>
                ))}
                {(() => {
                  const groupMap = new Map();
                  entries.forEach(e => {
                    (e.photos || []).forEach(p => {
                      if (!(p.latitude && p.longitude)) return;
                      const key = e.zone_id ? `zone-${e.zone_id}` : `entry-${e.id}`;
                      if (!groupMap.has(key)) {
                        groupMap.set(key, {
                          zoneId: e.zone_id || null,
                          label: e.zone_name || 'Monitoring Entry',
                          color: zoneColorMap[e.zone_id] || '#e65100',
                          photos: [],
                        });
                      }
                      groupMap.get(key).photos.push({
                        id: p.id,
                        url: getPhotoUrl(p.photo_url),
                        type: p.photo_type,
                        lat: Number(p.latitude),
                        lng: Number(p.longitude),
                        zoneName: e.zone_name,
                        entryDate: e.monitoring_date,
                      });
                    });
                  });
                  const groups = [];
                  groupMap.forEach((g, key) => {
                    if (g.photos.length === 0) return;
                    const cLat = g.photos.reduce((s, p) => s + p.lat, 0) / g.photos.length;
                    const cLng = g.photos.reduce((s, p) => s + p.lng, 0) / g.photos.length;
                    groups.push({ key, centroidLat: cLat, centroidLng: cLng, ...g });
                  });
                  return groups.map(g => {
                    const isSelected = selectedZoneId && g.zoneId === selectedZoneId;
                    const size = isSelected ? 34 : 26;
                    const count = g.photos.length;
                    return (
                      <Marker key={g.key} position={[g.centroidLat, g.centroidLng]}
                        icon={L.divIcon({
                          className: '',
                          html: `<div style="background:${g.color};color:#fff;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 0 6px rgba(0,0,0,0.5);font-size:${size >= 30 ? 13 : 11}px;font-weight:700;${isSelected ? 'animation:pulse 1s infinite;' : ''}">${count}</div>`,
                          iconSize: [size, size],
                          iconAnchor: [size/2, size/2],
                        })}>
                        <Popup maxWidth={320} minWidth={280}>
                          <PhotoCarousel
                            photos={g.photos}
                            label={g.label}
                            centroidLat={g.centroidLat}
                            centroidLng={g.centroidLng}
                          />
                        </Popup>
                      </Marker>
                    );
                  });
                })()}
              </MapContainer>
            </div>
            {/* Clickable zone legend */}
            <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
              <span onClick={() => setSelectedZoneId(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: !selectedZoneId ? '#e8f5e9' : '#fff', border: !selectedZoneId ? '2px solid #4caf50' : '1px solid #e0e0e0', fontWeight: !selectedZoneId ? 700 : 400 }}>
                All Zones
              </span>
              {zones.map((z) => (
                <span key={z.id} onClick={() => setSelectedZoneId(selectedZoneId === z.id ? null : z.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: selectedZoneId === z.id ? zoneColorMap[z.id] + '22' : '#fff', border: selectedZoneId === z.id ? `2px solid ${zoneColorMap[z.id]}` : '1px solid #e0e0e0', fontWeight: selectedZoneId === z.id ? 700 : 400 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: zoneColorMap[z.id], display: 'inline-block' }} />
                  {z.name} ({(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees)
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Monitoring Zone Cards */}
      {target.monitoring_zones && target.monitoring_zones.length > 0 && (() => {
        const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
        return (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring Zones <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>— click a zone to see it on the map</span></h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {target.monitoring_zones.map((z, i) => {
              const isActive = selectedZoneId === z.id;
              const zColor = zoneColors[i % zoneColors.length];
              return (
              <div key={z.id} onClick={() => { setSelectedZoneId(isActive ? null : z.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{ border: isActive ? `2px solid ${zColor}` : '1px solid #e0e0e0', borderRadius: 12, padding: 16, background: isActive ? zColor + '0a' : '#fafafa', cursor: 'pointer', transition: 'all 0.25s', transform: isActive ? 'scale(1.02)' : 'scale(1)', boxShadow: isActive ? `0 4px 16px ${zColor}33` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, background: zColor, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: '#1b5e20', fontSize: 15 }}>{z.name}</span>
                  {isActive && <span style={{ marginLeft: 'auto', fontSize: 11, background: zColor, color: '#fff', padding: '2px 8px', borderRadius: 10 }}>Viewing on map</span>}
                </div>
                {z.description && <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{z.description}</div>}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                  <span>Trees: <strong>{(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()}</strong></span>
                  {z.area_sqm && <span>Area: <strong>{Number(z.area_sqm).toLocaleString()}</strong> sq m</span>}
                  <span style={{ textTransform: 'capitalize', color: '#888' }}>{z.zone_type?.replace('_', ' ')}</span>
                </div>
                {z.boundary_polygon ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#4caf50', fontWeight: 600 }}>GPS Boundary Mapped ({z.boundary_polygon.length} points)</div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#ff9800' }}>GPS boundary not mapped yet</div>
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>{z.entries_count || 0} monitoring {(z.entries_count || 0) === 1 ? 'entry' : 'entries'}</div>
              </div>
            );})}
          </div>
        </div>
      );})()}

      {/* Monitoring Entries with Photos */}
      {target.monitoring_entries && target.monitoring_entries.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring History</h3>
          {target.monitoring_entries.map(e => {
            const healthColors = { excellent: '#1b5e20', good: '#4caf50', fair: '#ff9800', poor: '#f44336', critical: '#b71c1c' };
            const photos = e.photos || [];
            return (
              <div key={e.id} style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: 16, marginBottom: 16, background: '#fafffe' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong style={{ fontSize: 14 }}>{e.zone_name || 'Unknown Zone'}</strong>
                    <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{e.monitoring_date ? new Date(e.monitoring_date).toLocaleDateString() : ''}</span>
                    {e.agency_name && <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>by {e.agency_name}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: healthColors[e.overall_health] || '#666', background: (healthColors[e.overall_health] || '#666') + '18', textTransform: 'capitalize' }}>{e.overall_health}</span>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: statusColors[e.status] || '#666', background: (statusColors[e.status] || '#666') + '18' }}>{e.status}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>Trees: <strong>{(e.total_trees_monitored || 0).toLocaleString()}</strong></span>
                  <span>Healthy: <strong style={{ color: '#4caf50' }}>{e.trees_healthy || 0}</strong></span>
                  <span>Attention: <strong style={{ color: '#ff9800' }}>{e.trees_needs_attention || 0}</strong></span>
                  <span>Critical: <strong style={{ color: '#f44336' }}>{e.trees_critical || 0}</strong></span>
                  <span>Dead: <strong style={{ color: '#999' }}>{e.trees_dead || 0}</strong></span>
                  {e.survival_rate != null && <span>Survival: <strong>{e.survival_rate}%</strong></span>}
                </div>
                {e.notes && <div style={{ marginTop: 8, fontSize: 13, color: '#555', fontStyle: 'italic' }}>{e.notes}</div>}
                {e.latitude && e.longitude && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#1976d2' }}>
                    📍 Entry GPS: {Number(e.latitude).toFixed(6)}, {Number(e.longitude).toFixed(6)}
                  </div>
                )}
                {photos.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>Photos ({photos.length})</div>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                      {photos.map((p, pIdx) => (
                        <div key={p.id} onClick={() => { setLightboxPhotos(photos); setLightboxIndex(pIdx); setLightboxEntry(e.zone_name || ''); }}
                          style={{ flexShrink: 0, textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ position: 'relative', width: 100, height: 75 }}>
                            <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type}
                              style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8, border: '2px solid #e0e0e0' }}
                              onError={ev => { ev.target.style.display = 'none'; }} />
                            {p.latitude && p.longitude && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 8, padding: '2px 3px', borderRadius: '0 0 6px 6px', lineHeight: 1.2, textAlign: 'center' }}>
                                {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: '#888', marginTop: 2, textTransform: 'capitalize' }}>{p.photo_type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {/* Summary stats (planted count from project) */}
          {(() => {
            const entries = target.monitoring_entries;
            const totalMonitored = target.plants_reported || 0;
            const _latApproved = entries.filter(e => e.status === 'approved');
            const _treesSum = _latApproved.reduce((s, e) => s + (e.total_trees_monitored || 0), 0);
            const _wt = _latApproved.reduce((s, e) => s + (e.total_trees_monitored || 0) * (e.survival_rate || 0), 0);
            const avgSurvival = _treesSum > 0 ? (_wt / _treesSum).toFixed(1) : null;
            const totalPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);
            return (
              <div style={{ display: 'flex', gap: 24, marginTop: 8, padding: '12px 16px', background: '#f5f5f5', borderRadius: 10, fontSize: 13, flexWrap: 'wrap' }}>
                <span>Total Visits: <strong>{entries.length}</strong></span>
                <span>Trees Monitored: <strong>{totalMonitored.toLocaleString()}</strong></span>
                {avgSurvival && <span>Avg Survival: <strong>{avgSurvival}%</strong></span>}
                <span>Photos: <strong>{totalPhotos}</strong></span>
              </div>
            );
          })()}
        </div>
      )}

      {/* Empty state */}
      {(!target.milestones || target.milestones.length === 0) && (!target.reports || target.reports.length === 0) && (!target.monitoring_entries || target.monitoring_entries.length === 0) && (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: '#888' }}>
          No milestones, reports, disbursements, or monitoring data yet for this project.
        </div>
      )}
      {lightboxPhotos && <PhotoLightbox photos={lightboxPhotos} initialIndex={lightboxIndex} entryInfo={lightboxEntry} onClose={() => setLightboxPhotos(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project Assignments (Allocations) Management
// ---------------------------------------------------------------------------
function ProjectAssignments() {
  const [allocations, setAllocations] = React.useState([]);
  const [agencies, setAgencies] = React.useState([]);
  const [targets, setTargets] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [editItem, setEditItem] = React.useState(null);
  const [form, setForm] = React.useState({ target_id: '', agency_user_id: '', allocated_plants: '', allocated_budget: '', status: 'allocated' });
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const [aRes, agRes, tRes] = await Promise.all([
        adminAPI.getAllocations(),
        adminAPI.getAgencies(),
        adminAPI.getTargetsList(),
      ]);
      setAllocations(aRes.data.items || aRes.data.allocations || aRes.data || []);
      setAgencies(agRes.data.agencies || agRes.data || []);
      setTargets(tRes.data.targets || tRes.data || []);
    } catch (e) { toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditItem(null); setForm({ target_id: '', agency_user_id: '', allocated_plants: '', allocated_budget: '', status: 'allocated' }); setShowModal(true); };
  const openEdit = (a) => { setEditItem(a); setForm({ target_id: a.target_id, agency_user_id: a.agency_user_id, allocated_plants: a.allocated_plants || '', allocated_budget: a.allocated_budget || '', status: a.status || 'allocated' }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.target_id || !form.agency_user_id) { toast.error('Project and Agency are required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await adminAPI.updateAllocation(editItem.id, { allocated_plants: parseInt(form.allocated_plants) || 0, allocated_budget: parseFloat(form.allocated_budget) || 0, status: form.status });
      } else {
        await adminAPI.createAllocation({ target_id: parseInt(form.target_id), agency_user_id: parseInt(form.agency_user_id), allocated_plants: parseInt(form.allocated_plants) || 0, allocated_budget: parseFloat(form.allocated_budget) || 0 });
      }
      toast.success(editItem ? 'Assignment updated' : 'Assignment created');
      setShowModal(false); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assignment?')) return;
    try { await adminAPI.deleteAllocation(id); toast.success('Deleted'); load(); } catch { toast.error('Failed to delete'); }
  };

  const fmt = (n) => (n || 0).toLocaleString('en-IN');
  const statusColors = { allocated: '#1976d2', in_progress: '#f59e0b', completed: '#16a34a', cancelled: '#ef4444' };
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1b5e20' }}>Project Assignments</h2>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>Assign projects to implementing agencies</p>
        </div>
        <button onClick={openNew} style={{ background: '#1b5e20', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>+ New Assignment</button>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Assignments', value: allocations.length, color: '#1976d2' },
          { label: 'In Progress', value: allocations.filter(a => a.status === 'in_progress').length, color: '#f59e0b' },
          { label: 'Completed', value: allocations.filter(a => a.status === 'completed').length, color: '#16a34a' },
          { label: 'Total Plants Assigned', value: fmt(allocations.reduce((s, a) => s + (a.allocated_plants || 0), 0)), color: '#2e7d32' },
        ].map((k, i) => (
          <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Project', 'Agency', 'Plants', 'Budget', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allocations.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No assignments yet. Click "+ New Assignment" to create one.</td></tr>
            ) : allocations.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '12px 14px', fontWeight: 500 }}>{a.target_title || targets.find(t => t.id === a.target_id)?.title || `Project #${a.target_id}`}</td>
                <td style={{ padding: '12px 14px' }}>{a.agency_name || agencies.find(ag => ag.id === a.agency_user_id)?.organisation_name || `Agency #${a.agency_user_id}`}</td>
                <td style={{ padding: '12px 14px' }}>{fmt(a.allocated_plants)}</td>
                <td style={{ padding: '12px 14px' }}>{a.allocated_budget ? `₹${parseFloat(a.allocated_budget).toLocaleString('en-IN')}` : '-'}</td>
                <td style={{ padding: '12px 14px' }}><span style={{ background: (statusColors[a.status] || '#999') + '18', color: statusColors[a.status] || '#999', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{a.status}</span></td>
                <td style={{ padding: '12px 14px' }}>
                  <button onClick={() => openEdit(a)} style={{ background: 'none', border: '1px solid #d1d5db', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, marginRight: 6 }}>Edit</button>
                  <button onClick={() => handleDelete(a.id)} style={{ background: 'none', border: '1px solid #fca5a5', color: '#dc2626', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 480, maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', color: '#1b5e20' }}>{editItem ? 'Edit Assignment' : 'New Assignment'}</h3>
            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={labelStyle}>Project *</label>
                <select value={form.target_id} onChange={e => setForm({ ...form, target_id: e.target.value })} style={inputStyle} disabled={!!editItem}>
                  <option value="">Select project...</option>
                  {targets.map(t => <option key={t.id} value={t.id}>{t.title} — {t.city}, {t.state}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Agency *</label>
                <select value={form.agency_user_id} onChange={e => setForm({ ...form, agency_user_id: e.target.value })} style={inputStyle} disabled={!!editItem}>
                  <option value="">Select agency...</option>
                  {agencies.map(a => <option key={a.id} value={a.id}>{a.organisation_name || a.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Plants Allocated</label>
                  <input type="number" value={form.allocated_plants} onChange={e => setForm({ ...form, allocated_plants: e.target.value })} placeholder="e.g. 50000" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Budget (₹)</label>
                  <input type="number" value={form.allocated_budget} onChange={e => setForm({ ...form, allocated_budget: e.target.value })} placeholder="e.g. 5000000" style={inputStyle} />
                </div>
              </div>
              {editItem && (
                <div>
                  <label style={labelStyle}>Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
                    {['allocated', 'in_progress', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '10px 20px', border: 'none', borderRadius: 8, background: '#1b5e20', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving...' : editItem ? 'Update' : 'Create Assignment'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Zone Management
// ---------------------------------------------------------------------------
function AdminZoneManagement() {
  const [zones, setZones] = React.useState([]);
  const [targets, setTargets] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filterTarget, setFilterTarget] = React.useState('');
  const [showModal, setShowModal] = React.useState(false);
  const [editItem, setEditItem] = React.useState(null);
  const [form, setForm] = React.useState({ target_id: '', name: '', description: '', zone_type: 'pre_defined', tree_count: '', center_latitude: '', center_longitude: '', area_sqm: '' });
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const [zRes, tRes] = await Promise.all([
        adminAPI.getZones(filterTarget ? { target_id: filterTarget } : {}),
        adminAPI.getTargetsList(),
      ]);
      setZones(zRes.data.zones || zRes.data || []);
      setTargets(tRes.data.targets || tRes.data || []);
    } catch (e) { toast.error('Failed to load zones'); }
    setLoading(false);
  }, [filterTarget]);

  React.useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditItem(null); setForm({ target_id: filterTarget || '', name: '', description: '', zone_type: 'pre_defined', tree_count: '', center_latitude: '', center_longitude: '', area_sqm: '' }); setShowModal(true); };
  const openEdit = (z) => { setEditItem(z); setForm({ target_id: z.target_id, name: z.name, description: z.description || '', zone_type: z.zone_type || 'pre_defined', tree_count: z.tree_count || '', center_latitude: z.center_latitude || '', center_longitude: z.center_longitude || '', area_sqm: z.area_sqm || '' }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.name) { toast.error('Zone name is required'); return; }
    if (!editItem && !form.target_id) { toast.error('Project is required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await adminAPI.updateZone(editItem.id, { name: form.name, tree_count: parseInt(form.tree_count) || 0, description: form.description });
      } else {
        await adminAPI.createZone({ target_id: parseInt(form.target_id), name: form.name, description: form.description, zone_type: form.zone_type, tree_count: parseInt(form.tree_count) || 0, center_latitude: parseFloat(form.center_latitude) || null, center_longitude: parseFloat(form.center_longitude) || null, area_sqm: parseFloat(form.area_sqm) || null });
      }
      toast.success(editItem ? 'Zone updated' : 'Zone created');
      setShowModal(false); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this zone and all its monitoring entries?')) return;
    try { await adminAPI.deleteZone(id); toast.success('Zone deleted'); load(); } catch { toast.error('Failed to delete'); }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1b5e20' }}>Zone Management</h2>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>Create and manage monitoring zones for all projects</p>
        </div>
        <button onClick={openNew} style={{ background: '#1b5e20', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>+ New Zone</button>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: 16 }}>
        <select value={filterTarget} onChange={e => { setFilterTarget(e.target.value); setLoading(true); }} style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, minWidth: 300 }}>
          <option value="">All Projects</option>
          {targets.map(t => <option key={t.id} value={t.id}>{t.title} — {t.city}, {t.state}</option>)}
        </select>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Zones', value: zones.length, color: '#1976d2' },
          { label: 'Active', value: zones.filter(z => z.status === 'active').length, color: '#16a34a' },
          { label: 'Total Trees in Zones', value: zones.reduce((s, z) => s + (z.tree_count || 0), 0).toLocaleString('en-IN'), color: '#2e7d32' },
        ].map((k, i) => (
          <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Zone Name', 'Project', 'Type', 'Trees', 'Status', 'Created', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zones.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No zones found. Click "+ New Zone" to create one.</td></tr>
            ) : zones.map(z => (
              <tr key={z.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '12px 14px', fontWeight: 500 }}>{z.name}</td>
                <td style={{ padding: '12px 14px' }}>{z.target_title || targets.find(t => t.id === z.target_id)?.title || '-'}</td>
                <td style={{ padding: '12px 14px' }}><span style={{ background: z.zone_type === 'pre_defined' ? '#e8f5e9' : '#fff3e0', color: z.zone_type === 'pre_defined' ? '#2e7d32' : '#e65100', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>{z.zone_type === 'pre_defined' ? 'Pre-defined' : 'Ad-hoc'}</span></td>
                <td style={{ padding: '12px 14px' }}>{(z.tree_count || 0).toLocaleString('en-IN')}</td>
                <td style={{ padding: '12px 14px' }}><span style={{ color: z.status === 'active' ? '#16a34a' : '#94a3b8', fontWeight: 600, fontSize: 12 }}>{z.status}</span></td>
                <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b' }}>{z.created_at ? new Date(z.created_at).toLocaleDateString() : '-'}</td>
                <td style={{ padding: '12px 14px' }}>
                  <button onClick={() => openEdit(z)} style={{ background: 'none', border: '1px solid #d1d5db', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, marginRight: 6 }}>Edit</button>
                  <button onClick={() => handleDelete(z.id)} style={{ background: 'none', border: '1px solid #fca5a5', color: '#dc2626', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 500, maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', color: '#1b5e20' }}>{editItem ? 'Edit Zone' : 'New Zone'}</h3>
            <div style={{ display: 'grid', gap: 14 }}>
              {!editItem && (
                <div>
                  <label style={labelStyle}>Project *</label>
                  <select value={form.target_id} onChange={e => setForm({ ...form, target_id: e.target.value })} style={inputStyle}>
                    <option value="">Select project...</option>
                    {targets.map(t => <option key={t.id} value={t.id}>{t.title} — {t.city}, {t.state}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={labelStyle}>Zone Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Plot 1" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe this zone..." style={{ ...inputStyle, minHeight: 60 }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Zone Type</label>
                  <select value={form.zone_type} onChange={e => setForm({ ...form, zone_type: e.target.value })} style={inputStyle} disabled={!!editItem}>
                    <option value="pre_defined">Pre-defined</option>
                    <option value="ad_hoc">Ad-hoc</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Tree Count</label>
                  <input type="number" value={form.tree_count} onChange={e => setForm({ ...form, tree_count: e.target.value })} placeholder="0" style={inputStyle} />
                </div>
              </div>
              {!editItem && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Center Latitude</label>
                      <input type="number" step="0.0001" value={form.center_latitude} onChange={e => setForm({ ...form, center_latitude: e.target.value })} placeholder="e.g. 17.3850" style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Center Longitude</label>
                      <input type="number" step="0.0001" value={form.center_longitude} onChange={e => setForm({ ...form, center_longitude: e.target.value })} placeholder="e.g. 78.4867" style={inputStyle} />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Area (sq meters)</label>
                    <input type="number" value={form.area_sqm} onChange={e => setForm({ ...form, area_sqm: e.target.value })} placeholder="e.g. 5000" style={inputStyle} />
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '10px 20px', border: 'none', borderRadius: 8, background: '#1b5e20', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving...' : editItem ? 'Update Zone' : 'Create Zone'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Admin Dashboard
// ---------------------------------------------------------------------------
export default function AdminDashboard() {
  return (
    <DashboardLayout navItems={navItems} title="Admin Dashboard">
      <Routes>
        <Route path="" element={<Overview />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="targets" element={<TargetModeration />} />
        <Route path="targets/:id" element={<AdminTargetDetail />} />
        <Route path="reports" element={<ReportReview />} />
        <Route path="ideas" element={<IdeaModeration />} />
        <Route path="blog" element={<BlogManagement />} />
        <Route path="blog-moderation" element={<BlogModeration />} />
        <Route path="disbursements" element={<Disbursements />} />
        <Route path="group-monitoring" element={<GroupMonitoringReview />} />
        <Route path="monitoring-progress" element={<AdminMonitoringProgress />} />
        <Route path="allocations" element={<ProjectAssignments />} />
        <Route path="zone-management" element={<AdminZoneManagement />} />
        <Route path="species" element={<AdminSpecies />} />
        <Route path="species-report" element={<SpeciesReport />} />
        <Route path="field-supervisors" element={<AdminFieldSupervisors />} />
      </Routes>
    </DashboardLayout>
  );
}
